import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { BEREICH_FARBWERTE, type Clip, type Exportbereich, type Marke, type Op, PEGEL_MAX, offeneJeClip, Track, abschnitte, filmFuerAbschnitt, lohnendeAbschnitte, filmstripGridOf, auswahlZug, gruppenZug, mitGruppe, kurvenHoehe, markeBei, bereichAus, neuerBereich, zwischenraeume, randStellen, lueckeZu, type Zwischenraum, filmstripIndex, freieStelle, fuellFaktor, keyframeAt, snapClipStart, nachruecken, snapToFrame, sortedClips, spaetestensMitNachruecken, timelineDuration, trimGrenzen, uebergangsStellen, uebergangsZiel, umordnen, umordnenZiel, zieheGainKeyframe } from '@schnitt/shared';
import { SPUR_FLACH_BIS, SPUR_MIN, SPUR_NORMAL, TONKURVE_HOEHE, useEditor } from '../store';
import { zaehleBild } from '../messung';
import { api, type AssetView, type KommentarView } from '../api';
import { Icon } from './Icon';
import { ContextMenu, type MenuItem } from './ContextMenu';
import { Griff } from './Griff';
import { duplicateClip, splitAt } from '../edits';
import { newId } from '../ids';
import { clipMenuItems } from '../clipMenu';
import { useZeitText } from '../zeit';
import { useFreierKopf } from '../freierKopf';
import { brauchbar, hochladen, ziehtDateien } from '../hochladen';
import { platziere } from '../platzieren';
import type { SheetName } from './Toolbar';

/**
 * Der Anteil, den die Bühne von dem behält, was sie sich mit der Zeitleiste
 * teilt (7.111).
 *
 * Kein fester Punktwert: ein kleines Telefon hätte damit fast keine
 * Zeitleiste mehr, ein grosser Schirm eine Bühne, die kleiner ist als nötig.
 * 0,55 ist die Aufteilung, die vier Spuren auf einem iPhone 13 ohnehin
 * ergeben -- wer bis vier Spuren bleibt, merkt von der Grenze nichts.
 */
const BUEHNE_ANTEIL = 0.55;

/**
 * Was die Zeitleiste ausser den Spuren braucht: die Zeitskala und die Zeile
 * zum Anlegen einer neuen Spur.
 */
const RAHMEN = 24 + 30;

/**
 * The timeline, built for a thumb.
 *
 * The playhead stays in the middle and the cut moves underneath it: dragging
 * anywhere scrolls, and scrolling *is* scrubbing. That is the gesture every
 * phone editor uses, and it gives scrubbing the momentum and precision of
 * native scrolling for free. A tap selects a clip; only a selected clip can
 * be dragged, so a drag across the cut never accidentally moves something.
 *
 * Nothing commits to the document until a gesture ends, so a drag produces
 * one undo step and one broadcast rather than sixty.
 */

const SNAP_PIXELS = 10;
const DRAG_THRESHOLD = 6;
/** Room after the last clip: the add tile, and somewhere to scroll to. */
const TAIL = 120;
/** A finger held this long without moving is asking for the menu. */
const LONG_PRESS_MS = 450;
/** Die Miniaturen beim Umordnen der Hauptspur (15. Oktober): Kante und Abstand von Platz zu Platz. */
const UMORDNEN_KACHEL = 56;
const UMORDNEN_SCHRITT = 64;
/** Wie nah am Rand der Finger die Reihe weiterrollen lässt. */
const UMORDNEN_RAND = 48;

/**
 * Wie weit die Reihe der Miniaturen liegen darf: der linke Rand des ersten
 * Platzes zwischen „erste Miniatur am linken Rand“ und „letzte am rechten“.
 * Passt die Reihe auf den Schirm, liegt sie ganz darauf, und `rollt` ist
 * falsch -- dann gibt es nichts zu rollen.
 */
function umordnenGrenzen(breite: number, anzahl: number): { min: number; max: number; rollt: boolean } {
  const links = 16;
  const rechts = breite - 16 - UMORDNEN_KACHEL - (anzahl - 1) * UMORDNEN_SCHRITT;
  return { min: Math.min(links, rechts), max: Math.max(links, rechts), rollt: rechts < links };
}
/**
 * Der senkrechte Weg für den vollen Pegel, in Pixeln. Ein Ton-Clip auf einer
 * flachen Spur ist gut zwanzig Pixel hoch -- über zwanzig Pixel den ganzen
 * Bereich von still bis doppelt zu legen, hieße: jedes Zittern ein anderer
 * Ton. Darum gilt die Höhe des Clips, aber mindestens dieser Weg; auf einem
 * schmalen Clip zieht der Finger dann weiter, als der Punkt wandert.
 */
const PEGEL_WEG = 48;

type GestureKind = 'move' | 'trim-start' | 'trim-end' | 'keyframe' | 'pegel';

interface Gesture {
  kind: GestureKind;
  pointerId: number;
  clipId: string;
  /** The track the clip came from. */
  trackId: string;
  startX: number;
  startY: number;
  originalClip: Clip;
  /** For a keyframe or level-point drag: where in the clip it sat when it began. */
  keyframeAt: number;
  /** Für den Zug an einem Pegelpunkt: sein Pegel beim Aufsetzen des Fingers. */
  pegelGain: number;
  /** Und die Höhe des Clips auf dem Schirm -- daran hängt, wie weit der
   *  Finger für den vollen Pegelweg wandern muss. */
  clipHoehe: number;
  moved: boolean;
  /**
   * Where each track sits on screen, measured when the drag begins. The rows
   * do not move while a finger is down -- the timeline scrolls sideways --
   * so measuring once beats measuring on every frame.
   */
  rows: { trackId: string; top: number; bottom: number }[];
}

/**
 * A clip belongs on the kind of track it is: sound on a sound track,
 * everything with a picture on a picture track. Dropping a voice-over onto
 * V1 would silently do nothing at the export, so the drag refuses instead.
 */
function canDrop(clip: Clip, track: Track, assetKind: 'video' | 'audio' | 'image' | undefined): boolean {
  const wantsAudio = clip.source.kind === 'asset' && assetKind === 'audio';
  return wantsAudio ? track.kind === 'audio' : track.kind === 'video';
}

export function Timeline({ onAdd, onOpenSheet }: { onAdd: () => void; onOpenSheet: (sheet: SheetName) => void }): ReactElement {
  const zeit = useZeitText();
  zaehleBild('zeitleiste');
  const timeline = useEditor((s) => s.timeline);
  const assets = useEditor((s) => s.assets);
  /**
   * Was von der Vorschau gerechnet ist (7.71b) -- Abschnitt für Abschnitt.
   * Sie steht nur da, wenn überhaupt etwas gerechnet oder in Arbeit ist: eine
   * Leiste, die immer rot ist, sagt nichts.
   */
  const filmRenders = useEditor((s) => s.renders);
  const filmAssets = useEditor((s) => s.assets);
  const filmleiste = useMemo(() => {
    // Nur die Abschnitte, die einen Film überhaupt brauchen (7.71d): eine
    // Leiste, die überall rot ist, wo nichts zu holen wäre, lügt.
    const videos = new Set(filmAssets.filter((a) => a.kind === 'video').map((a) => a.id));
    const liste = lohnendeAbschnitte(timeline, abschnitte(timeline), (id: string) => videos.has(id)).map((a) => ({
      von: a.von,
      bis: a.bis,
      stand: (filmFuerAbschnitt(filmRenders, a)
        ? 'fertig'
        : filmRenders.some((r) => r.abdruck === a.abdruck && (r.status === 'queued' || r.status === 'running'))
          ? 'rechnet'
          : 'fehlt') as 'fertig' | 'rechnet' | 'fehlt',
    }));
    return liste.some((a) => a.stand !== 'fehlt') ? liste : [];
  }, [timeline, filmRenders, filmAssets]);
  const nachrueckenAn = useEditor((s) => s.nachruecken);
  // Auf der Hauptspur einer magnetischen Zeitleiste (7.201) rückt immer alles
  // nach, und ein gezogener Clip weicht nicht in die nächste Lücke aus: wo er
  // losgelassen wird, ordnet er sich ein, und der Magnet zieht den Rest glatt.
  const magnetSpur = timeline.settings.magnetisch ? timeline.tracks.find((t) => t.kind === 'video')?.id : undefined;
  const rueckt = (trackId: string): boolean => nachrueckenAn || trackId === magnetSpur;
  const zoom = useEditor((s) => s.zoom);
  const setZoom = useEditor((s) => s.setZoom);
  const setPlayhead = useEditor((s) => s.setPlayhead);
  const playing = useEditor((s) => s.playing);
  const setPlaying = useEditor((s) => s.setPlaying);
  const selection = useEditor((s) => s.selection);
  const select = useEditor((s) => s.select);
  const multiSelect = useEditor((s) => s.multiSelect);
  const toggleSelect = useEditor((s) => s.toggleSelect);
  const keyframeWahl = useEditor((s) => s.keyframeWahl);
  const waehleKeyframe = useEditor((s) => s.waehleKeyframe);
  const setEinfuegeStelle = useEditor((s) => s.setEinfuegeStelle);
  const setMultiSelect = useEditor((s) => s.setMultiSelect);
  const dispatch = useEditor((s) => s.dispatch);
  const projektId = useEditor((s) => s.project?.id ?? '');
  const refreshAssets = useEditor((s) => s.refreshAssets);
  const peers = useEditor((s) => s.peers);
  const role = useEditor((s) => s.role);
  const uebergangsModus = useEditor((s) => s.uebergangsModus);

  const scroller = useRef<HTMLDivElement>(null);
  const gesture = useRef<Gesture | null>(null);
  const pinch = useRef<{ distance: number; zoom: number } | null>(null);
  /** The scroll position we set ourselves, so its scroll event is not read as scrubbing. */
  const programmatic = useRef<number | null>(null);
  /** The playhead the last user scroll produced, so we do not scroll to it again. */
  const scrolledTo = useRef<number | null>(null);
  const [centre, setCentre] = useState(0);
  /** Am Desktop wandert der Kopf über die stehende Zeitleiste (7.206). */
  const frei = useFreierKopf();
  /** Fährt die Maus gerade darüber (7.207)? Ein Ja oder Nein, kein Zeitwert. */
  const ueberfaehrt = useEditor((s) => s.ueberfahrenVon !== null);
  const [preview, setPreview] = useState<{ clipId: string; clip: Clip; trackId: string; kollision?: boolean; auswahl?: boolean } | null>(null);
  /**
   * The long press. iOS does not raise `contextmenu` for a held finger, so
   * the hold is timed by hand: armed after LONG_PRESS_MS without movement,
   * and the menu opens when the finger *lifts* -- opening under a finger
   * that is still down turns its release into a tap on the menu.
   */
  const press = useRef<{
    timer: ReturnType<typeof setTimeout>;
    pointerId: number;
    clipId: string;
    x: number;
    y: number;
    armed: boolean;
    /** Was angehoben ist, falls der Finger nach dem Halten zieht (7.204). */
    clip: Clip;
    trackId: string;
  } | null>(null);
  const [pressing, setPressing] = useState<string | null>(null);
  /**
   * **Umordnen wie in CapCut** (Sascha, 15. Oktober: „Videos verschieben:
   * drauf bleiben und ziehen sollten so Miniaturen kommen oder?“ -- „Ja, wie
   * CapCut“). Lang gedrückt und gezogen, klappt die Hauptspur zu gleich
   * grossen Miniaturen zusammen; die gezogene folgt dem Finger, die anderen
   * machen Platz, und losgelassen steht der Clip an seinem neuen Platz
   * (`umordnen` rechnet). Nur mit dem Finger und nur auf der Hauptspur: auf
   * den Ebenen darüber zählt die Zeit, nicht die Reihe, und die Maus zieht
   * am Schreibtisch wie bisher (7.204).
   *
   * Alles in Punkten der Zeitleisten-Hülle: `x` ist der Finger, `anfang` der
   * linke Rand des ersten Platzes, `mitte` die Höhe der Reihe.
   */
  const [umordnung, setUmordnung] = useState<{ clipId: string; pointerId: number; von: number; x: number; anfang: number; mitte: number } | null>(null);
  const umordnungRef = useRef(umordnung);
  umordnungRef.current = umordnung;
  const [menu, setMenu] = useState<{ clipId: string; x: number; y: number; at: number } | null>(null);
  const [trackMenu, setTrackMenu] = useState<{ trackId: string; x: number; y: number } | null>(null);
  /** Das Menü der Zeitleiste selbst (S5): Marken und Bereich hängen an der
   *  Zeit, nicht an einem Clip -- also gehören sie an die Leiste. */
  const [leistenMenu, setLeistenMenu] = useState<{ x: number; y: number; at: number } | null>(null);
  /** Das `+` in einem Zwischenraum wurde angetippt (7.215): wo, und was dort ist. */
  const [zwischenMenu, setZwischenMenu] = useState<{ x: number; y: number; trackId: string; raum: Zwischenraum } | null>(null);
  const [markeName, setMarkeName] = useState<{ markeId: string; name: string } | null>(null);
  const bereich = useEditor((s) => s.bereich);
  const setBereich = useEditor((s) => s.setBereich);
  const [umbenennen, setUmbenennen] = useState<{ trackId: string; name: string } | null>(null);
  /** 7.14: Einen Clip umbenennen -- dasselbe kleine Blatt wie bei der Spur. */
  const clipName = useEditor((s) => s.clipUmbenennen);
  const setClipName = useEditor((s) => s.setClipUmbenennen);
  /** Und die Angaben zum Medium dahinter: eine Auskunft, kein Regler. */
  const angaben = useEditor((s) => s.clipAngaben);
  const setAngaben = useEditor((s) => s.setClipAngaben);
  const setHinweisAnker = useEditor((s) => s.setHinweisAnker);
  const setErsetzen = useEditor((s) => s.setErsetzen);
  const kommentare = useEditor((s) => s.kommentare);
  // Wie viele offene Hinweise an jedem Clip hängen (Sascha: „Es muss am Objekt
  // hingeklebt sein“). Einmal je Bild gerechnet und nicht je Clip -- sonst
  // liefe die Liste einmal für jeden Clip durch.
  const hinweiseJeClip = useMemo(
    () => offeneJeClip(kommentare.map((k) => ({ clipId: k.clip_id, parentId: k.parent_id, erledigt: !!k.resolved_at }))),
    [kommentare],
  );
  // Wie hoch die Spuren stehen, entscheidet der Griff in der Transportleiste
  // -- deshalb liegt es im Speicher und nicht hier.
  const spurHoehe = useEditor((s) => s.spurHoehe);
  const tonkurven = useEditor((s) => s.tonkurven);
  const tonkurveUmschalten = useEditor((s) => s.tonkurveUmschalten);
  /**
   * Wie hoch eine Spur ist: die eingestellte Höhe, und bei aufgeklappter
   * Tonkurve eine Bahn mehr (7.65). Sie steht *unter* dem Filmstreifen und
   * nimmt ihm nichts weg -- man soll beides zugleich sehen, sonst schneidet
   * man nach dem Ton und weiß nicht mehr, welches Bild dazugehört.
   */
  /**
   * Wie viel Platz die Zeitleiste haben darf (7.111).
   *
   * Sie war bis zum 18. September unbegrenzt: jede Spur machte sie höher, und
   * die Höhe nahm sie der Bühne darüber. Auf einem iPhone 13 gemessen --
   * vier Spuren: Leinwand 127x226; sechs Spuren: 75x134; zehn Spuren:
   * **2x2**. Das Bild ist dann nicht klein, es ist weg, und wer auf dem
   * Telefon mehr als vier Spuren anlegt, schneidet blind.
   *
   * Die Bühne behält deshalb ihren Anteil an der Höhe, und was die Zeitleiste
   * mehr bräuchte, holt sie sich aus flacheren Spuren -- bis hinunter zu
   * `SPUR_MIN`. Die eingestellte Höhe bleibt dabei stehen: fällt eine Spur
   * weg, stehen die anderen wieder so hoch wie gewählt.
   */
  const wrapRef = useRef<HTMLDivElement>(null);
  const [platz, setPlatz] = useState<number | null>(null);
  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    const app = wrap?.parentElement;
    const buehne = app?.querySelector<HTMLElement>('.buehne');
    if (!wrap || !buehne) return;
    const messen = () => {
      // Gerechnet wird mit dem, was sich Bühne und Zeitleiste *teilen*, nicht
      // mit der ganzen Seite. Diese Summe ändert sich nicht, wenn die
      // Zeitleiste flacher wird -- die Bühne bekommt genau so viel dazu --,
      // und deshalb schaukelt sich die Rechnung nicht auf. Sie gilt so auch
      // am grossen Schirm, wo neben der Bühne noch eine Spalte steht.
      const zusammen = buehne.offsetHeight + wrap.offsetHeight;
      const frei = Math.max(0, zusammen - Math.round(zusammen * BUEHNE_ANTEIL));
      // Nicht auf den Punkt genau: ein, zwei Punkte Unterschied kommen beim
      // Abspielen ständig vor (eine Leiste, die sich einblendet, ein
      // Rollbalken), und jede Änderung wäre ein neues Bild der ganzen
      // Zeitleiste. Am 18. September hat der schnelle Durchgang genau das
      // gemeldet: 22 Bilder statt höchstens 20. Erst ab acht Punkten ist es
      // eine Änderung -- eine halbe Spur.
      setPlatz((vorher) => (vorher !== null && Math.abs(vorher - frei) < 8 ? vorher : frei));
    };
    messen();
    const beobachter = new ResizeObserver(messen);
    beobachter.observe(app!);
    window.addEventListener('resize', messen);
    return () => {
      beobachter.disconnect();
      window.removeEventListener('resize', messen);
    };
  });
  // Gerechnet wird mit dem grösseren Rahmen (aufgeklappt, mit der Zeile zum
  // Anlegen). Führt das Ergebnis dazu, dass die Spuren als eingeklappt
  // gelten, ist die Leiste danach etwas niedriger als erlaubt -- und nicht
  // höher. Ein Rahmen, der sich mit dem Ergebnis ändert, würde hin und her
  // springen.
  //
  // **Eine aufgeklappte Tonkurve zählt hier nicht mit**, obwohl sie Höhe
  // kostet. Sie gehört zu *einer* Spur und ist ein Handgriff für einen
  // Moment; rechnete man sie mit, stellte das Aufklappen einer Kurve alle
  // anderen Spuren flach und das Zuklappen sie wieder hoch -- ein Sprung
  // durch die ganze Leiste für einen Doppeltipp. Gemessen am 18. September:
  // 46 → 34 → 46 Punkte, ohne dass eine Spur dazukam. Die Kurve nimmt ihre
  // 56 Punkte weiter von der Bühne, wie bisher.
  const passendeHoehe =
    platz === null || timeline.tracks.length === 0
      ? spurHoehe
      : Math.floor((platz - RAHMEN) / timeline.tracks.length);
  // **Wer den Griff nach oben zieht, hat gesagt, was er will.** Die Grenze
  // gilt der Normalhöhe und allem darunter -- also dem Zustand, in dem
  // niemand etwas eingestellt hat und einfach eine Spur dazukam. Eine Höhe
  // über der Normalhöhe ist eine Entscheidung: dort *soll* die Zeitleiste der
  // Bühne Platz nehmen, und der Griff darf nicht ins Leere gehen (am
  // 18. September zuerst genau das: 30 Punkte gezogen, 12 Punkte gewachsen).
  const grenze = spurHoehe > SPUR_NORMAL ? spurHoehe : passendeHoehe;
  const spurHoeheEff = Math.max(SPUR_MIN, Math.min(spurHoehe, grenze));
  const hoeheVon = (trackId: string, kind: string): number =>
    spurHoeheEff + (kind === 'video' && tonkurven.includes(trackId) ? TONKURVE_HOEHE : 0);
  const eingeklappt = spurHoeheEff < SPUR_FLACH_BIS;
  const toast = useEditor((s) => s.toast);
  /** The label's own hold, separate from the clips': it opens the track menu. */
  const labelPress = useRef<{ timer: ReturnType<typeof setTimeout>; pointerId: number; x: number; y: number; armed: boolean; trackId: string } | null>(null);

  const assetById = useMemo(() => new Map(assets.map((a) => [a.id, a])), [assets]);
  const fps = timeline.settings.fps;
  const end = timelineDuration(timeline);
  const contentWidth = end * zoom + centre * 2 + TAIL;

  // Half the viewport is where zero sits when the playhead is at zero.
  // Mit freiem Kopf am Desktop (7.206) steht die Null links, gleich hinter
  // den Spurnamen -- die halbe Breite brauchte nur die feste Linie.
  useEffect(() => {
    const element = scroller.current;
    if (!element) return;
    const measure = () => setCentre(frei ? Math.min(element.clientWidth / 2, FREI_LINKS) : element.clientWidth / 2);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [frei]);

  /**
   * **Alles zeigen** (S40; Sascha, 18. September, mit einem Bildschirmfoto,
   * auf dem neben den Zoomknöpfen ein „Fit“ sitzt). Mit fester Mitte (am
   * Telefon) passt der Film in die *halbe* Breite: dann liegt er von jeder
   * Stelle des Kopfes aus ganz im Bild, und weder die feste Mitte noch die
   * Stelle, an der man gerade arbeitet, geht dafür verloren. Mit freiem Kopf
   * (am Desktop) füllt er die Breite und steht am Anfang. Ein zweiter Tipp
   * holt den Zoom von vorher zurück.
   */
  const vorherZoom = useRef<number | null>(null);
  const passenderZoom = (): number | null => {
    const element = scroller.current;
    if (!element || end <= 0) return null;
    const platz = frei ? element.clientWidth - centre - 24 : centre - 12;
    return Math.max(0.1, Math.min(400, platz / end));
  };
  const zeigtAlles = vorherZoom.current !== null && Math.abs(zoom - (passenderZoom() ?? -1)) <= zoom * 0.01;
  const allesZeigen = (): void => {
    const ziel = passenderZoom();
    if (ziel === null) return;
    if (zeigtAlles && vorherZoom.current !== null) {
      setZoom(vorherZoom.current);
      vorherZoom.current = null;
      return;
    }
    vorherZoom.current = zoom;
    setZoom(ziel);
    if (frei) requestAnimationFrame(() => scroller.current?.scrollTo({ left: 0 }));
  };

  const timeAt = useCallback(
    (clientX: number): number => {
      const box = scroller.current?.getBoundingClientRect();
      const scroll = scroller.current?.scrollLeft ?? 0;
      return Math.max(0, (clientX - (box?.left ?? 0) + scroll - centre) / zoom);
    },
    [zoom, centre],
  );

  /** Den freien Kopf am Dreieck ziehen (7.206): er folgt der Maus, auf ganze Bilder. */
  const kopfGreifen = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    const griff = e.currentTarget;
    griff.setPointerCapture(e.pointerId);
    if (useEditor.getState().playing) setPlaying(false);
    // Wer den Kopf greift, überfährt nicht mehr (7.207): sonst zöge er ihn,
    // und die Linie stünde weiter am alten Platz.
    useEditor.getState().ueberfahrenEnde(true);
    const setze = (x: number) => setPlayhead(snapToFrame(Math.max(0, Math.min(end, timeAt(x))), fps));
    setze(e.clientX);
    const zug = (ev: PointerEvent) => setze(ev.clientX);
    const los = () => {
      griff.removeEventListener('pointermove', zug);
      griff.removeEventListener('pointerup', los);
      griff.removeEventListener('pointercancel', los);
    };
    griff.addEventListener('pointermove', zug);
    griff.addEventListener('pointerup', los);
    griff.addEventListener('pointercancel', los);
  };

  // Scrolling is scrubbing. Our own scrollTo calls come back through here too
  // and are recognised by their target, so playback does not fight itself.
  const onScroll = () => {
    const element = scroller.current;
    if (!element) return;
    // Wie weit gerollt ist, für die Linie des freien Kopfes (7.206).
    wrapRef.current?.style.setProperty('--roll-x', `${element.scrollLeft}px`);
    if (frei) {
      // Am Desktop ist Rollen nur Rollen: der Kopf bleibt, wo er ist.
      programmatic.current = null;
      return;
    }
    if (programmatic.current !== null) {
      if (Math.abs(element.scrollLeft - programmatic.current) < 1.5) {
        programmatic.current = null;
        return;
      }
      // A user scroll interrupted ours (a flick during playback, say).
      programmatic.current = null;
    }
    if (useEditor.getState().playing) return;
    const t = snapToFrame(Math.min(end, element.scrollLeft / zoom), fps);
    scrolledTo.current = t;
    setPlayhead(t);
  };

  // The other direction: the playhead moved (play, step, jump, a peer) and the
  // cut has to slide under it.
  //
  // Zugehört statt mitgezeichnet (7.27): beim Abspielen wandert der Kopf
  // sechzigmal in der Sekunde. Läse diese Stelle ihn aus dem Speicher,
  // zeichnete sich die ganze Zeitleiste sechzigmal neu -- alle Spuren, alle
  // Clips, alle Filmstreifen. Gebraucht wird davon nichts: unter der
  // feststehenden Linie schiebt sich der Schnitt vorbei, und das ist ein
  // Scrollwert und kein neues Bild.
  useEffect(() => {
    const rolle = (t: number) => {
      const element = scroller.current;
      if (!element) return;
      // Wo der Kopf im Inhalt steht, für das `+` in freien Zeilen (7.229)
      // und die Linie des freien Kopfes (7.206): ein Wert an der Hülle, kein
      // neues Bild.
      const huelle = wrapRef.current ?? element;
      // Beim Überfahren (7.207) bleibt die Linie, wo der Kopf wirklich steht;
      // eine zweite, dünne zeigt, wo die Maus ist.
      const von = useEditor.getState().ueberfahrenVon;
      huelle.style.setProperty('--kopf-x', `${(frei && von !== null ? von : t) * zoom}px`);
      huelle.style.setProperty('--skim-x', `${t * zoom}px`);
      huelle.style.setProperty('--roll-x', `${element.scrollLeft}px`);
      if (frei && von !== null) return;
      if (frei) {
        // Am Desktop rollt die Zeitleiste nur, wenn der Kopf aus dem Bild
        // liefe -- dann so, dass er bei einem Viertel steht, wie in jedem
        // Schnittprogramm, das weiterblättert.
        const x = centre + t * zoom - element.scrollLeft;
        if (x < 0 || x > element.clientWidth - 24) {
          const ziel = Math.max(0, Math.round(centre + t * zoom - element.clientWidth * 0.25));
          programmatic.current = ziel;
          element.scrollLeft = ziel;
          huelle.style.setProperty('--roll-x', `${element.scrollLeft}px`);
        }
        return;
      }
      if (scrolledTo.current !== null && Math.abs(scrolledTo.current - t) < 1e-6) {
        scrolledTo.current = null;
        return;
      }
      const target = Math.round(t * zoom);
      if (Math.abs(element.scrollLeft - target) < 1) return;
      programmatic.current = target;
      element.scrollLeft = target;
    };
    rolle(useEditor.getState().playhead);
    return useEditor.subscribe((jetzt, vorher) => {
      if (jetzt.playhead !== vorher.playhead || jetzt.ueberfahrenVon !== vorher.ueberfahrenVon) rolle(jetzt.playhead);
    });
  }, [zoom, frei, centre]);

  /**
   * Candidate edges to stick to: clip boundaries, the playhead and zero.
   *
   * Der Kopf wird erst *beim Ziehen* geholt und nicht bei jedem Bild
   * mitgeführt: gebraucht wird er in dem Augenblick, in dem ein Clip an ihm
   * einrastet, und in dem steht er still.
   */
  const snapPoints = useCallback((): number[] => {
    const points = new Set<number>([0, useEditor.getState().playhead]);
    for (const track of timeline.tracks) {
      for (const clip of track.clips) {
        points.add(clip.start);
        points.add(clip.start + clip.duration);
      }
    }
    return [...points];
  }, [timeline]);

  /**
   * Moving a whole clip sticks with either edge: the head to a cut, or the
   * tail to the next clip's head. That is what makes two clips land exactly
   * one behind the other instead of a frame apart or a frame over.
   */
  const snapMove = useCallback(
    (start: number, duration: number): number =>
      snapToFrame(snapClipStart(start, duration, snapPoints(), SNAP_PIXELS / zoom), fps),
    [snapPoints, zoom, fps],
  );

  const snap = useCallback(
    (seconds: number): number => {
      const tolerance = SNAP_PIXELS / zoom;
      let best = seconds;
      let bestDelta = tolerance;
      for (const point of snapPoints()) {
        const delta = Math.abs(point - seconds);
        if (delta < bestDelta) {
          best = point;
          bestDelta = delta;
        }
      }
      return snapToFrame(Math.max(0, best), fps);
    },
    [snapPoints, zoom, fps],
  );

  /**
   * **Dateien auf die Zeitleiste fallen lassen** (Sascha, 15. Oktober:
   * „Drag&drop als Datei muss gehen! … Drop Felder: Timeline Position“). Die
   * Linie folgt der Maus und rastet an Schnitten und am Kopf ein wie ein
   * gezogener Clip; die Spur ist die unter der Maus. Losgelassen wird
   * hochgeladen, und was fertig ist, kommt an genau diese Stelle --
   * dahinter rückt nach, wie beim `+` (7.156).
   */
  const [ablage, setAblage] = useState<{ t: number; spur: string | null } | null>(null);
  const ablageAus = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ablageZiehen = (e: React.DragEvent<HTMLDivElement>): void => {
    if (!ziehtDateien(e) || role === 'viewer') return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    const t = snap(Math.min(end, timeAt(e.clientX)));
    const spur = (e.target as Element).closest('[data-track-id]')?.getAttribute('data-track-id') ?? null;
    setAblage((alt) => (alt && alt.t === t && alt.spur === spur ? alt : { t, spur }));
    // Ein Ziehen, das die Zeitleiste verlässt oder abgebrochen wird, meldet
    // sich nicht immer ab; ohne neues `dragover` geht die Linie von selbst.
    if (ablageAus.current) clearTimeout(ablageAus.current);
    ablageAus.current = setTimeout(() => setAblage(null), 250);
  };
  const ablegen = (e: React.DragEvent<HTMLDivElement>): void => {
    if (!ziehtDateien(e) || role === 'viewer') return;
    e.preventDefault();
    const t = ablage?.t ?? snap(Math.min(end, timeAt(e.clientX)));
    const spur = ablage?.spur ?? null;
    setAblage(null);
    const { gut, weg } = brauchbar(e.dataTransfer.files);
    if (weg.length) toast('error', `${weg.map((n) => `„${n}“`).join(', ')}: kein Video, Ton oder Bild.`);
    if (gut.length) void hochladen(gut, t, spur);
  };

  const clearPress = () => {
    if (press.current) clearTimeout(press.current.timer);
    press.current = null;
    setPressing(null);
  };

  const openMenu = (clipId: string, x: number, y: number) => {
    if (!selection.includes(clipId)) select([clipId]);
    setMenu({ clipId, x, y, at: timeAt(x) });
  };

  const onClipPointerDown = (event: React.PointerEvent, kind: GestureKind, clip: Clip, trackId: string, keyframeAt = 0, pegelGain = 1, clipHoehe = 0) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (role === 'viewer') return;
    // Mit Umschalt, Befehl oder Strg wird gewählt und nicht gezogen (7.205):
    // der Klick nimmt den Clip zur Auswahl dazu oder heraus.
    if (event.pointerType === 'mouse' && kind === 'move' && (event.shiftKey || event.metaKey || event.ctrlKey)) {
      event.stopPropagation();
      return;
    }
    if (kind === 'move') {
      clearPress();
      const { clientX, clientY, pointerId } = event;
      press.current = {
        pointerId,
        clipId: clip.id,
        clip,
        trackId,
        x: clientX,
        y: clientY,
        armed: false,
        timer: setTimeout(() => {
          if (!press.current || press.current.pointerId !== pointerId) return;
          press.current.armed = true;
          setPressing(clip.id);
          // Android has this; iOS has no vibration API in the browser and
          // shows the pressed state instead.
          navigator.vibrate?.(8);
        }, LONG_PRESS_MS),
      };
    }
    // **Direkt ziehen** (7.204; Sascha, 4. Oktober: „Auch drag&drop muss
    // direkt gehen“). Mit der Maus zieht jeder Clip sofort und wird dabei
    // gewählt. Mit dem Finger ist ein Zug über einen nicht gewählten Clip
    // weiter ein Rollen der Zeitleiste -- er wird erst nach dem langen Druck
    // angehoben (`onPointerMove`).
    if (!selection.includes(clip.id)) {
      if (event.pointerType !== 'mouse' || kind !== 'move' || multiSelect) return;
      select([clip.id]);
    }
    (event.target as Element).setPointerCapture?.(event.pointerId);
    zugAnfangen(kind, event.pointerId, event.clientX, event.clientY, clip, trackId, keyframeAt, pegelGain, clipHoehe);
    event.stopPropagation();
  };

  /** Ein Zug beginnt: am gewählten Clip, mit der Maus, oder nach dem langen Druck. */
  const zugAnfangen = (
    kind: GestureKind,
    pointerId: number,
    startX: number,
    startY: number,
    clip: Clip,
    trackId: string,
    keyframeAt = 0,
    pegelGain = 1,
    clipHoehe = 0,
  ) => {
    gesture.current = {
      kind,
      pointerId,
      clipId: clip.id,
      trackId,
      startX,
      startY,
      originalClip: structuredClone(clip),
      keyframeAt,
      pegelGain,
      clipHoehe,
      moved: false,
      rows: [...(scroller.current?.querySelectorAll<HTMLElement>('[data-track-id]') ?? [])].map((row) => {
        const box = row.getBoundingClientRect();
        return { trackId: row.dataset.trackId ?? '', top: box.top, bottom: box.bottom };
      }),
    };
  };

  const onPointerMove = (event: React.PointerEvent) => {
    const u = umordnungRef.current;
    if (u && u.pointerId === event.pointerId) {
      const links = wrapRef.current?.getBoundingClientRect().left ?? 0;
      setUmordnung({ ...u, x: event.clientX - links });
      return;
    }
    // A finger that wanders is scrolling or dragging, not asking for a menu.
    const p = press.current;
    if (p && p.pointerId === event.pointerId && Math.hypot(event.clientX - p.x, event.clientY - p.y) > 8) {
      // Nach dem langen Druck ist der Clip angehoben: der Zug schiebt ihn,
      // statt ein Menü zu öffnen (7.204). Auf der Hauptspur, mit dem Finger,
      // ordnet er um -- als Miniatur in der Reihe.
      if (p.armed && !gesture.current && role !== 'viewer') {
        select([p.clipId]);
        (event.target as Element).setPointerCapture?.(event.pointerId);
        const basis = timeline.tracks.find((t) => t.kind === 'video');
        if (event.pointerType !== 'mouse' && basis && !basis.locked && p.trackId === basis.id && basis.clips.length > 1) {
          const huelle = wrapRef.current?.getBoundingClientRect();
          const zeile = scroller.current?.querySelector(`[data-track-id="${basis.id}"]`)?.getBoundingClientRect();
          const von = sortedClips(basis).findIndex((c) => c.id === p.clipId);
          const links = huelle?.left ?? 0;
          const grenzen = umordnenGrenzen(huelle?.width ?? 0, basis.clips.length);
          setUmordnung({
            clipId: p.clipId,
            pointerId: p.pointerId,
            von,
            x: event.clientX - links,
            // Die Reihe liegt so, dass der Platz des gezogenen Clips unter dem
            // Finger ist -- soweit sie dabei auf dem Schirm bleibt.
            anfang: Math.max(grenzen.min, Math.min(grenzen.max, p.x - links - von * UMORDNEN_SCHRITT - UMORDNEN_KACHEL / 2)),
            mitte: zeile && huelle ? zeile.top - huelle.top + zeile.height / 2 : (huelle?.height ?? 0) / 2,
          });
        } else {
          zugAnfangen('move', p.pointerId, p.x, p.y, p.clip, p.trackId);
        }
      }
      clearPress();
    }
    const g = gesture.current;
    if (!g || g.pointerId !== event.pointerId) return;
    const dx = event.clientX - g.startX;
    const dy = event.clientY - g.startY;
    if (!g.moved && Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
    // A finger that starts on a keyframe but pulls up or down wants the clip
    // on another track -- the keyframes sit on the clip's top edge, which is
    // exactly where one grabs it to lift it. The first movement decides, and
    // only once: after that the gesture stays what it became.
    if (!g.moved && g.kind === 'keyframe' && Math.abs(dy) > Math.abs(dx)) g.kind = 'move';
    g.moved = true;

    const original = g.originalClip;
    const deltaSeconds = dx / zoom;

    if (g.kind === 'move' && selection.length > 1 && selection.includes(original.id)) {
      // **Die ganze Auswahl zieht** (7.205): jeder Clip auf seiner Spur, alle
      // um denselben Betrag. Die Zeile wechselt dabei keiner -- welche Spur
      // wäre für fünf Clips von drei Spuren „die darunter“? Ob es passt,
      // sagt `auswahlZug`, dasselbe, was beim Loslassen zieht.
      const start = Math.max(0, snapToFrame(snapMove(original.start + deltaSeconds, original.duration), fps));
      const passt = auswahlZug(timeline, selection, start - original.start) !== null;
      setPreview({ clipId: original.id, trackId: g.trackId, clip: { ...original, start }, auswahl: true, ...(passt ? {} : { kollision: true }) });
    } else if (g.kind === 'move') {
      // Which row is the finger over? The one it started on when it is over
      // none, or over one this clip may not go on.
      const row = g.rows.find((r) => event.clientY >= r.top && event.clientY <= r.bottom);
      const target = row ? timeline.tracks.find((t) => t.id === row.trackId) : undefined;
      const assetKind = original.source.kind === 'asset' ? assetById.get(original.source.assetId)?.kind : undefined;
      const trackId = target && canDrop(original, target, assetKind) ? target.id : g.trackId;
      // Auf einer Spur liegt nichts übereinander: erst an die Nachbarn
      // einrasten, dann aus jeder Überdeckung in die nächste Lücke rutschen.
      // Das passiert schon in der Vorschau, damit man beim Ziehen sieht, wo
      // der Clip wirklich landet.
      const nachbarn = (timeline.tracks.find((t) => t.id === trackId)?.clips ?? []).filter((c) => c.id !== original.id);
      const gewuenscht = snapMove(original.start + deltaSeconds, original.duration);
      const ausweich = trackId === magnetSpur ? Math.max(0, gewuenscht) : freieStelle(gewuenscht, original.duration, nachbarn);
      if (ausweich === null) {
        // Kein Platz: der Clip bleibt sichtbar, wo er ist, und der Streifen
        // sagt es. Ihn irgendwohin zu schicken -- hinter einen zehn Minuten
        // langen Nachbarn etwa -- hieße, ihn zu verlieren.
        setPreview({ clipId: original.id, trackId: g.trackId, clip: original, kollision: true });
      } else {
        setPreview({ clipId: original.id, trackId, clip: { ...original, start: snapToFrame(ausweich, fps) } });
      }
    } else if (g.kind === 'keyframe') {
      // A keyframe belongs to the clip, so it moves in the clip's own seconds
      // and cannot leave it: dragging one past the end would animate to a
      // moment that is never played.
      const at = Math.min(original.duration, Math.max(0, snapToFrame(g.keyframeAt + deltaSeconds, fps)));
      const keyframes = original.keyframes.map((k) => (k.at === g.keyframeAt ? { ...k, at } : k));
      setPreview({ clipId: original.id, trackId: g.trackId, clip: { ...original, keyframes } });
    } else if (g.kind === 'pegel') {
      // Waagerecht die Zeit, senkrecht der Pegel. Der senkrechte Weg misst
      // sich an der Höhe des Clips -- aber an mindestens PEGEL_WEG Pixeln:
      // auf einer flachen Tonspur wäre der ganze Pegelweg sonst zwölf Pixel
      // lang, und jedes Zittern ein anderer Ton.
      const at = snapToFrame(g.keyframeAt + deltaSeconds, fps);
      const gain = g.pegelGain - (dy / Math.max(g.clipHoehe, PEGEL_WEG)) * PEGEL_MAX;
      const gainKeyframes = zieheGainKeyframe(original.gainKeyframes, g.keyframeAt, { at, gain }, original.duration);
      setPreview({ clipId: original.id, trackId: g.trackId, clip: { ...original, gainKeyframes } });
    } else if (g.kind === 'trim-start') {
      // Nicht ueber den Nachbarn (Sascha, 8. September: „kann ich es ueber
      // ein anderes drueber ziehen. Das ist unlogisch“). Die Grenzen kommen
      // aus `trimmen.ts`; beim Verschieben prueft `freieStelle` denselben
      // Platz, beim Trimmen fehlte die Pruefung bisher.
      const grenzen = trimGrenzen(timeline.tracks.find((t) => t.id === g.trackId)?.clips ?? [], original.id);
      // Mit Nachrücken (S1) ist der Nachbar keine Grenze mehr -- er weicht ja
      // aus. Was bleibt, ist die Quelle: weiter als an ihren Anfang kann der
      // Kopf nicht zurück.
      const fruehestens = rueckt(g.trackId)
        ? original.start - original.sourceIn / original.speed
        : grenzen.fruehestens;
      // Trimming the head moves the in-point in the source too, so the frame
      // under the cut stays the same frame.
      const proposed = Math.max(fruehestens, snap(original.start + deltaSeconds));
      const maxStart = original.start + original.duration - 1 / fps;
      const start = Math.min(proposed, maxStart);
      const shift = start - original.start;
      setPreview({
        clipId: original.id,
        trackId: g.trackId,
        clip: {
          ...original,
          // Beim Nachrücken bleibt der Clip stehen, wo er anfing: die Lücke
          // vor ihm soll ja gerade nicht entstehen. Verändert wird nur, wie
          // viel von der Quelle zu sehen ist.
          start: rueckt(g.trackId) ? original.start : start,
          duration: Math.max(1 / fps, original.duration - shift),
          sourceIn: Math.max(0, original.sourceIn + shift * original.speed),
        },
      });
    } else {
      const grenzen = trimGrenzen(timeline.tracks.find((t) => t.id === g.trackId)?.clips ?? [], original.id);
      const quelle =
        original.source.kind === 'asset'
          ? (assetById.get(original.source.assetId)?.durationSec ?? null)
          : null;
      const spaetestens = rueckt(g.trackId)
        ? spaetestensMitNachruecken(original, quelle)
        : grenzen.spaetestens;
      const endAt = Math.min(spaetestens, snap(original.start + original.duration + deltaSeconds));
      setPreview({
        clipId: original.id,
        trackId: g.trackId,
        clip: { ...original, duration: Math.max(1 / fps, endAt - original.start) },
      });
    }
  };

  const endGesture = (event: React.PointerEvent) => {
    const u = umordnungRef.current;
    if (u && u.pointerId === event.pointerId) {
      setUmordnung(null);
      if (event.type !== 'pointerup') return;
      const anzahl = timeline.tracks.find((t) => t.kind === 'video')?.clips.length ?? 0;
      const ops = umordnen(timeline, u.clipId, umordnenZiel(u.x, u.anfang, UMORDNEN_SCHRITT, UMORDNEN_KACHEL, anzahl));
      if (ops.length) dispatch(ops);
      return;
    }
    const p = press.current;
    if (p && p.pointerId === event.pointerId) {
      const armed = p.armed;
      clearPress();
      if (armed && event.type === 'pointerup') {
        gesture.current = null;
        setPreview(null);
        openMenu(p.clipId, p.x, p.y);
        return;
      }
    }
    const g = gesture.current;
    gesture.current = null;
    if (!g || g.pointerId !== event.pointerId) return;

    const pending = preview;
    setPreview(null);
    if (!pending || !g.moved) return;
    if (pending.kollision) {
      toast('info', pending.auswahl ? 'Dort ist kein Platz für die ganze Auswahl.' : 'Dort ist kein Platz.');
      return;
    }

    const before = g.originalClip;
    const after = pending.clip;
    const ops: Op[] = [];
    if (g.kind === 'move' && pending.auswahl) {
      const zug = after.start !== before.start ? auswahlZug(timeline, selection, after.start - before.start) : [];
      if (zug === null) {
        toast('info', 'Dort ist kein Platz für die ganze Auswahl.');
        return;
      }
      for (const z of zug) ops.push({ op: 'moveClip', clipId: z.clipId, toTrackId: z.trackId, start: z.start });
    } else if (g.kind === 'move' && (after.start !== before.start || pending.trackId !== g.trackId)) {
      // Die Gruppe wandert mit (S3), um denselben Betrag und auf ihren eigenen
      // Spuren. Findet auch nur einer der Begleiter keinen Platz, geht der
      // ganze Zug nicht: eine halb bewegte Gruppe wäre genau das, was die
      // Gruppe verhindern soll.
      const begleiter = gruppenZug(timeline, after.id, before.start, after.start);
      if (begleiter === null) {
        toast('info', 'Dort ist kein Platz für die ganze Gruppe.');
        return;
      }
      ops.push({ op: 'moveClip', clipId: after.id, toTrackId: pending.trackId, start: after.start });
      for (const z of begleiter) {
        const spur = timeline.tracks.find((t) => t.clips.some((c) => c.id === z.clipId));
        if (spur) ops.push({ op: 'moveClip', clipId: z.clipId, toTrackId: spur.id, start: z.start });
      }
    } else if (g.kind === 'keyframe') {
      ops.push({ op: 'updateClip', clipId: after.id, patch: { keyframes: after.keyframes } });
      // Der gezogene Keyframe liegt jetzt woanders; die Wahl zeigt sonst auf
      // eine Stelle, an der keiner mehr ist.
      const gezogen = after.keyframes.find((k, i) => k.at !== before.keyframes[i]?.at);
      if (gezogen) waehleKeyframe({ clipId: after.id, at: gezogen.at });
    } else if (g.kind === 'pegel') {
      ops.push({ op: 'updateClip', clipId: after.id, patch: { gainKeyframes: after.gainKeyframes } });
    } else if (g.kind !== 'move') {
      ops.push({
        op: 'updateClip',
        clipId: after.id,
        patch: { start: after.start, duration: after.duration, sourceIn: after.sourceIn },
      });
      // Und was dahinter liegt, rückt nach (S1). Es zählt nur, um wie viel
      // sich die Länge geändert hat -- für beide Griffe dasselbe.
      if (rueckt(g.trackId)) {
        const spur = timeline.tracks.find((t) => t.id === g.trackId);
        for (const n of nachruecken(spur?.clips ?? [], before, after.duration - before.duration)) {
          ops.push({ op: 'moveClip', clipId: n.clipId, toTrackId: g.trackId, start: n.start });
        }
      }
    }
    if (ops.length) dispatch(ops);
  };

  // Beim Umordnen rollt die Reihe, solange der Finger am Rand steht: so
  // kommt man auch an Plätze, die nicht auf den Schirm passen.
  const umordnetGerade = umordnung !== null;
  useEffect(() => {
    if (!umordnetGerade) return;
    let bild = 0;
    const schritt = () => {
      const u = umordnungRef.current;
      const breite = wrapRef.current?.clientWidth ?? 0;
      const anzahl = useEditor.getState().timeline.tracks.find((t) => t.kind === 'video')?.clips.length ?? 0;
      const grenzen = umordnenGrenzen(breite, anzahl);
      if (u && breite > 0 && grenzen.rollt) {
        const um =
          u.x < UMORDNEN_RAND ? Math.min(10, (UMORDNEN_RAND - u.x) / 4) : u.x > breite - UMORDNEN_RAND ? -Math.min(10, (u.x - breite + UMORDNEN_RAND) / 4) : 0;
        if (um !== 0) {
          // Nicht weiter als bis die erste oder letzte Miniatur am Rand steht.
          const anfang = Math.max(grenzen.min, Math.min(grenzen.max, u.anfang + um));
          if (anfang !== u.anfang) setUmordnung({ ...u, anfang });
        }
      }
      bild = requestAnimationFrame(schritt);
    };
    bild = requestAnimationFrame(schritt);
    return () => cancelAnimationFrame(bild);
  }, [umordnetGerade]);

  // Ist ein Clip angehoben (7.204), rollt die Zeitleiste unter dem Finger
  // nicht mit. `touch-action` lässt sich mitten in einer Berührung nicht mehr
  // umstellen; ein `touchmove`, das vor dem ersten Rollen abgewiesen wird,
  // hält auch Safari an.
  useEffect(() => {
    const element = scroller.current;
    if (!element) return;
    const halt = (e: TouchEvent) => {
      if (press.current?.armed || gesture.current?.kind === 'move' || umordnungRef.current) e.preventDefault();
    };
    element.addEventListener('touchmove', halt, { passive: false });
    return () => element.removeEventListener('touchmove', halt);
  }, []);

  // Pinch to zoom, and ctrl/cmd + wheel for a laptop. Both keep the playhead
  // where it is on screen, because that is what the eye is on.
  //
  // Mit zwei Fingern über `touch*`, nicht über Zeiger (7.218; Sascha, 4.
  // Oktober: „Pinch to zoom in der Timeline geht noch nicht mobil, nur am
  // Desktop“). Zwei Gründe, beide nachgestellt: der Lauscher hing am Zoom
  // und wurde mit jedem Schritt neu angemeldet -- mit einer leeren Liste der
  // aufliegenden Finger, also blieb es bei einem Schritt (180 → 202 px statt
  // des Vierfachen). Und sobald der erste Finger die Zeitleiste rollt, bricht
  // der Browser seinen Zeiger ab (`touch-action: pan-x`); Berührungen laufen
  // weiter. Der Zoom wird deshalb frisch aus dem Laden gelesen.
  useEffect(() => {
    const element = scroller.current;
    if (!element) return;
    const abstand = (t: TouchList): number => {
      const [a, b] = [t[0], t[1]];
      return a && b ? Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY) : 0;
    };
    const start = (e: TouchEvent) => {
      if (e.touches.length !== 2) return;
      // Der zweite Finger macht aus allem, was der erste angefangen hat,
      // einen Zoom: kein Zug, kein langer Druck.
      gesture.current = null;
      if (press.current) clearTimeout(press.current.timer);
      press.current = null;
      setPressing(null);
      pinch.current = { distance: abstand(e.touches), zoom: useEditor.getState().zoom };
    };
    const move = (e: TouchEvent) => {
      const p = pinch.current;
      if (!p || e.touches.length !== 2 || p.distance <= 0) return;
      if (e.cancelable) e.preventDefault();
      setZoom(p.zoom * (abstand(e.touches) / p.distance));
    };
    const ende = (e: TouchEvent) => {
      if (e.touches.length < 2) pinch.current = null;
    };
    const wheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      setZoom(useEditor.getState().zoom * Math.exp(-e.deltaY * 0.01));
    };

    element.addEventListener('touchstart', start, { passive: true });
    element.addEventListener('touchmove', move, { passive: false });
    element.addEventListener('touchend', ende);
    element.addEventListener('touchcancel', ende);
    element.addEventListener('wheel', wheel, { passive: false });
    return () => {
      element.removeEventListener('touchstart', start);
      element.removeEventListener('touchmove', move);
      element.removeEventListener('touchend', ende);
      element.removeEventListener('touchcancel', ende);
      element.removeEventListener('wheel', wheel);
    };
  }, [setZoom]);

  const videoTracks = timeline.tracks.filter((t) => t.kind === 'video');
  const baseTrackId = videoTracks[0]?.id;

  /**
   * **Das `+` in einer freien Zeile, am Abspielkopf** (7.229; Sascha, 6.
   * Oktober, mit Bildschirmfoto: „Ich kann leider bei Overlay nichts
   * einfügen. Es Cuttet mir immer die untere Spur auseinander. Auch ein +
   * Zeichen fehlt dort mobil“). Eine leere Ebene hat keine Schnitte und
   * keine Lücken, also auch keines der Pluszeichen aus 7.215; eingefügt
   * wurde dann in die Hauptspur, mitten in ihren Clip.
   *
   * Jede Spur ausser der Hauptspur trägt deshalb ein `+` unter der
   * Abspiellinie, solange dort auf ihr nichts liegt -- und kein anderes Plus
   * daneben steht. Ein Ja oder Nein je Spur und keine Zahl: so zeichnet sich
   * die Zeitleiste nur neu, wenn eines kommt oder geht, nicht bei jedem Bild.
   */
  /**
   * Wo das Plus eines Zwischenraums steht, in Sekunden. Am Schnitt auf ihm;
   * in einer Lücke **vorn am nächsten Clip** und nicht in ihrer Mitte (7.234;
   * Sascha, 6. Oktober, mit Bildschirmfoto einer Lücke von 24 s: „warum ist
   * hier das + einen kilometer weg? :D“). `data-zwischen-bei` bleibt die
   * Mitte -- das ist die Lücke, die das Plus meint, nicht seine Lage.
   */
  const plusLage = (z: Zwischenraum): number => (z.art === 'schnitt' ? z.bei : Math.max((z.von + z.bis) / 2, z.bis - 18 / zoom));

  /** Wartet etwas auf seine Stelle (17. Oktober), zeigen die Plus, wohin. */
  const platzierenAn = useEditor((s) => s.platzieren);

  const freiAmKopf = useEditor((s) => {
    if (role === 'viewer' || s.uebergangsModus) return '';
    const kopf = s.ueberfahrenVon ?? s.playhead;
    const rand = 14 / zoom;
    return timeline.tracks
      .filter((t) => t.id !== baseTrackId && !t.locked)
      .filter((t) => !t.clips.some((c) => c.start < kopf + rand && c.start + c.duration > kopf - rand))
      .filter((t) => !zwischenraeume(t, fps).some((z) => Math.abs(plusLage(z) - kopf) < 26 / zoom))
      .filter((t) => !randStellen(t, fps).some((r) => Math.abs(r.bei - kopf) < 26 / zoom))
      .map((t) => t.id)
      .join(' ');
  });
  const spurPlus = new Set(freiAmKopf.split(' ').filter(Boolean));

  // Wer mit einer gezogenen Auswahl mitzieht (7.205), und um wie viel: die
  // anderen stehen dabei sichtbar dort, wo sie landen würden.
  const mitzieher = preview?.auswahl ? new Set(mitGruppe(timeline, selection)) : null;
  const ankerVorher = preview?.auswahl ? timeline.tracks.flatMap((t) => t.clips).find((c) => c.id === preview.clipId) : undefined;
  const versatz = preview?.auswahl && ankerVorher ? (preview.clip.start - ankerVorher.start) * zoom : 0;

  /**
   * **Ein Rahmen wählt** (7.205): mit der Maus über leerer Fläche aufgezogen,
   * wählt er jeden Clip, den er berührt -- mit Umschalt/Befehl/Strg dazu.
   */
  const [rahmen, setRahmen] = useState<{ pointerId: number; x0: number; y0: number; x: number; y: number; dazu: boolean } | null>(null);
  const rahmenGezogen = useRef(false);
  const rahmenEnde = (e: React.PointerEvent): void => {
    const r = rahmen;
    setRahmen(null);
    if (!r || Math.hypot(r.x - r.x0, r.y - r.y0) < 4) return;
    const links = Math.min(r.x0, e.clientX);
    const rechts = Math.max(r.x0, e.clientX);
    const oben = Math.min(r.y0, e.clientY);
    const unten = Math.max(r.y0, e.clientY);
    const ids = [...(scroller.current?.querySelectorAll('.clip[data-clip-id]') ?? [])]
      .filter((el) => {
        const k = el.getBoundingClientRect();
        return k.left < rechts && k.right > links && k.top < unten && k.bottom > oben;
      })
      .map((el) => el.getAttribute('data-clip-id')!);
    rahmenGezogen.current = true;
    select(r.dazu ? [...new Set([...selection, ...ids])] : ids);
  };


  /** V3, A2, ... -- the next label of its kind that is not taken. */
  const nextTrackId = (kind: Track['kind']): string => {
    const prefix = kind === 'video' ? 'V' : 'A';
    let n = 1;
    while (timeline.tracks.some((t) => t.id === `${prefix}${n}`)) n++;
    return `${prefix}${n}`;
  };

  /**
   * A new video track goes under the last video track; a new audio track
   * goes last. Keeping the kinds together is what makes the labels readable.
   */
  const addTrack = (kind: Track['kind']) => {
    const track = Track.parse({ id: nextTrackId(kind), kind });
    const lastVideo = timeline.tracks.map((t) => t.kind).lastIndexOf('video');
    const index = kind === 'video' ? lastVideo + 1 : undefined;
    // No toast: the new row appearing is the message.
    dispatch({ op: 'addTrack', track, ...(index !== undefined ? { index } : {}) } as Op);
  };

  /**
   * Was man an einer Stelle der Zeitleiste tun kann (S5).
   *
   * Sascha: „Stellen in der Zeitleiste benennen, und aus einem Bereich heraus
   * exportieren.“ Beides hängt an einem Zeitpunkt und nicht an einem Clip --
   * eine Marke bleibt, wo sie ist, auch wenn der Clip darunter ausgetauscht
   * wird, und ein Bereich fängt oft in einer Lücke an. Darum die Leiste und
   * nicht das Clip-Menü.
   */
  /**
   * Aus der Auswahl einen benannten Bereich machen und sein Blatt öffnen
   * (7.197) -- angeheftet oder fest, wie eingestellt, wie im Export-Blatt.
   */
  const benenneAuswahl = (): void => {
    if (!bereich) return;
    const b = {
      ...neuerBereich(timeline.exportbereiche, 'Bereich', bereich.von, bereich.bis),
      angeheftet: timeline.settings.bereicheAnheften !== false,
    };
    dispatch([{ op: 'addExportbereich', bereich: b }] as Op[]);
    useEditor.getState().setBereichBlatt(b.id);
    onOpenSheet('bereich');
  };

  const leistenMenuItems = (at: number): MenuItem[] => {
    const stelle = snapToFrame(Math.max(0, Math.min(end, at)), fps);
    const da = markeBei(timeline.marken, stelle, 1 / fps);
    // Die benannten Bereiche an dieser Stelle, zuoberst (7.196; Sascha,
    // 3. Oktober: „bereiche anklicken und optionen brauchen wir“). Bis dahin
    // öffnete nur der kleine Name im Band ihr Blatt; das Band selbst lässt
    // den Tipp durch, weil er auf dem Lineal den Abspielkopf setzt.
    const bereicheHier: MenuItem[] = timeline.exportbereiche
      .filter((b) => b.von <= stelle + 1e-6 && stelle <= b.bis + 1e-6)
      // Als Zeile mit Namen, nicht als Zeichen in der oberen Reihe (Sascha,
      // 4. Oktober, mit Bildschirmfoto: „Hier jetzt zwei Bereiche und zwei
      // ‚T‘“ -- „man muss halt irgendwie definieren woran man arbeiten will“).
      .map((b) => ({
        icon: 'text',
        label: `Bereich „${b.name}“ bearbeiten`,
        gruppe: 'clip',
        onSelect: () => {
          useEditor.getState().setBereichBlatt(b.id);
          onOpenSheet('bereich');
        },
      }));
    const inAuswahl =
      bereich && bereich.von <= stelle + 1e-6 && stelle <= bereich.bis + 1e-6 &&
      !timeline.exportbereiche.some((b) => Math.abs(b.von - bereich.von) < 0.02 && Math.abs(b.bis - bereich.bis) < 0.02);
    if (inAuswahl && role !== 'viewer') {
      bereicheHier.push({ icon: 'text', label: 'Auswahl benennen …', gruppe: 'clip', onSelect: benenneAuswahl });
    }
    const items: MenuItem[] = [
      ...bereicheHier,
      da
        ? {
            icon: 'minus',
            label: 'Marke wegnehmen',
            gruppe: 'medium',
            onSelect: () => {
              dispatch({ op: 'removeMarke', markeId: da.id } as Op);
              toast('success', 'Marke entfernt.');
            },
          }
        : {
            icon: 'plus',
            label: 'Marke hier setzen',
            gruppe: 'medium',
            onSelect: () => {
              const id = newId('m');
              dispatch({ op: 'addMarke', marke: { id, at: stelle, label: '' } } as Op);
              setPlayhead(stelle);
              // Gleich nach dem Namen fragen: eine Marke ohne Namen ist ein
              // Strich, und „Stellen benennen“ war die Bitte.
              setMarkeName({ markeId: id, name: '' });
            },
          },
    ];
    if (da) {
      items.push({
        icon: 'text',
        label: 'Marke benennen',
        gruppe: 'medium',
        onSelect: () => setMarkeName({ markeId: da.id, name: da.label }),
      });
    }
    items.push({
      icon: 'stepBack',
      label: 'Bereich beginnt hier',
      gruppe: 'clip',
      onSelect: () => {
        const neu = bereichAus(stelle, bereich?.bis ?? end, end, 1 / fps);
        if (!neu) { toast('info', 'Dahinter ist nichts mehr.'); return; }
        setBereich(neu);
      },
    });
    items.push({
      icon: 'stepForward',
      label: 'Bereich endet hier',
      gruppe: 'clip',
      onSelect: () => {
        const neu = bereichAus(bereich?.von ?? 0, stelle, end, 1 / fps);
        if (!neu) { toast('info', 'Davor ist nichts.'); return; }
        setBereich(neu);
      },
    });
    if (bereich) {
      items.push({
        // Nicht unter „Mehr“: steht ein Bereich, ist ihn aufzuheben die
        // naheliegendste nächste Handlung und gehört in die sichtbare Liste.
        icon: 'trash',
        label: 'Bereich aufheben',
        gruppe: 'clip',
        onSelect: () => setBereich(null),
      });
    }
    return items;
  };

  /**
   * **Was in einem Zwischenraum geht** (7.215; Sascha, 4. Oktober: „So kann
   * gefragt werden ob Übergang oder was einfügen“). An einem Schnitt ein
   * Übergang oder etwas dazwischen; in einer Lücke etwas hinein, oder sie
   * zu.
   */
  const zwischenItems = (trackId: string, raum: Zwischenraum): MenuItem[] => {
    const spur = timeline.tracks.find((t) => t.id === trackId);
    if (!spur) return [];
    const einfuegen = (bei: number, label: string): MenuItem => ({
      icon: 'plus',
      label,
      gruppe: 'clip',
      onSelect: () => {
        // Auf ein ganzes Bild (7.228): so rundet auch das Einfügen, und Kopf
        // und Eingefügtes stehen auf demselben Bild. Ein Schnitt zwischen zwei
        // Bildern -- nach einem Tempo etwa -- stellte den Kopf sonst ein Bild
        // vor das, was eingefügt wurde, und man sah es nicht.
        const b = snapToFrame(bei, fps);
        setPlayhead(b);
        setEinfuegeStelle(b);
        useEditor.getState().setEinfuegeSpur(trackId);
        onAdd();
      },
    });
    if (raum.art === 'schnitt') {
      return [
        ...(spur.kind === 'video'
          ? [{
              icon: 'transition',
              label: 'Übergang …',
              gruppe: 'clip' as const,
              onSelect: () => {
                select([raum.clipId]);
                setPlayhead(raum.bei);
                onOpenSheet('uebergaenge');
              },
            }]
          : []),
        einfuegen(raum.bei, 'Hier einfügen …'),
      ];
    }
    // Das Plus steht vorn am nächsten Clip (7.234), also zuerst das, was
    // man dort erwartet: direkt davor -- der Clip rückt dafür nach hinten.
    return [
      einfuegen(raum.bis, 'Direkt vor dem Clip einfügen …'),
      einfuegen(raum.von, 'In die Lücke einfügen …'),
      {
        icon: 'nachruecken',
        label: 'Lücke schließen',
        gruppe: 'clip',
        onSelect: () => {
          const ops = lueckeZu(spur, raum).map((z) => ({ op: 'moveClip', clipId: z.clipId, toTrackId: trackId, start: z.start }) as Op);
          if (ops.length) dispatch(ops);
        },
      },
    ];
  };

  const trackMenuItems = (trackId: string): MenuItem[] => {
    const track = timeline.tracks.find((t) => t.id === trackId);
    if (!track) return [];
    const isBase = trackId === baseTrackId;
    const items: MenuItem[] = [
      {
        icon: track.kind === 'audio' ? (track.muted ? 'volume' : 'mute') : track.hidden ? 'eye' : 'eyeOff',
        label: track.kind === 'audio' ? (track.muted ? 'Ton an' : 'Stummschalten') : track.hidden ? 'Einblenden' : 'Ausblenden',
        onSelect: () =>
          dispatch(
            track.kind === 'audio'
              ? ({ op: 'updateTrack', trackId, patch: { muted: !track.muted } } as Op)
              : ({ op: 'updateTrack', trackId, patch: { hidden: !track.hidden } } as Op),
          ),
      },
      {
        icon: track.locked ? 'check' : 'eyeOff',
        label: track.locked ? 'Spur entsperren' : 'Spur sperren',
        onSelect: () => dispatch({ op: 'updateTrack', trackId, patch: { locked: !track.locked } } as Op),
      },
      {
        icon: 'sliders',
        label: 'Spur einstellen',
        onSelect: () => {
          useEditor.getState().setSpurBlatt(trackId);
          onOpenSheet('spur');
        },
      },
      { icon: 'text', label: 'Spur umbenennen', onSelect: () => setUmbenennen({ trackId, name: track.name }) },
      { icon: 'plus', label: track.kind === 'audio' ? 'Tonspur hinzufügen' : 'Bildspur hinzufügen', onSelect: () => addTrack(track.kind) },
    ];
    if (track.kind === 'video') {
      // Schneiden nach Ton: die Kurve des Videos gross genug, um danach zu
      // schneiden (7.65). Eine Ansicht, kein Schnitt -- sie steht im Browser
      // und geht die anderen nichts an.
      items.splice(1, 0, {
        icon: 'volume',
        label: tonkurven.includes(track.id) ? 'Tonkurve einklappen' : 'Tonkurve ausklappen',
        onSelect: () => tonkurveUmschalten(track.id),
      });
    }
    if (track.kind === 'audio') {
      items.splice(1, 0, { icon: 'mic', label: 'Voiceover aufnehmen', onSelect: () => onOpenSheet('voiceover') });
      // A music bed: it drops whenever speech on another track is audible.
      items.splice(2, 0, {
        icon: 'music',
        label: track.duck ? 'Nicht mehr unter Sprache absenken' : 'Unter Sprache absenken',
        onSelect: () => dispatch({ op: 'updateTrack', trackId, patch: { duck: !track.duck } } as Op),
      });
    }
    if (!isBase) {
      items.push({
        icon: 'trash',
        label: track.clips.length ? `Spur entfernen (${track.clips.length} Clips drauf)` : 'Spur entfernen',
        danger: true,
        onSelect: () => {
          // Never silently: a track with clips on it is emptied first, by
          // hand, so nothing disappears from a tap on a menu.
          if (track.clips.length) {
            toast('info', 'Die Spur ist nicht leer.');
            return;
          }
          dispatch({ op: 'removeTrack', trackId } as Op);
        },
      });
    }
    return items;
  };

  const menuItems = (clipId: string, at: number): MenuItem[] =>
    clipMenuItems(
      {
        projectId: projektId,
        refreshAssets,
        timeline,
        settings: timeline.settings,
        fps,
        assetById,
        selection,
        dispatch,
        toast,
        select,
        setMultiSelect,
        setPlayhead,
        setErsetzen,
        setClipName,
        setAngaben,
        setHinweisAnker,
        onOpenSheet,
        tonkurven,
        tonkurveUmschalten,
      },
      clipId,
      at,
    );

  /** The track the clip being dragged came from, so its row is not the drop target. */
  const dragSourceTrackId = preview
    ? timeline.tracks.find((t) => t.clips.some((c) => c.id === preview.clipId))?.id
    : undefined;

  /**
   * What a row shows while a clip is being dragged over another one: the
   * clip leaves the row it came from and appears in the row under the
   * finger, so the drop is what you see before you let go.
   */
  const clipsOf = (track: Track): Clip[] => {
    const clips = sortedClips(track).filter((c) => !(preview && c.id === preview.clipId && preview.trackId !== track.id));
    if (preview && preview.trackId === track.id && !track.clips.some((c) => c.id === preview.clipId)) {
      return [...clips, preview.clip].sort((a, b) => a.start - b.start);
    }
    return clips;
  };

  // Eingeklappt werden die Spuren flacher -- alle bleiben da. Sascha:
  // „Kann man die Leinwand sichtbarer machen? Und die Timeline ausklappbar?“
  // und, zum ersten Versuch: „wie komm ich an die anderen Ebenen?“
  // Die Wahl gehört zum Gerät, nicht zum Projekt.
  // Eingeklappt werden die Spuren flacher, nicht weniger: Saschas Einwand --
  // „wie komm ich an die anderen Ebenen?“ Wer Platz für das Bild will, soll
  // nicht die Hälfte seines Schnitts verlieren. Bei vielen Spuren bleibt die
  // Leiste rollbar, statt beliebig flach zu werden.
  // Oben liegt, was im Bild oben liegt: die höchste Bildspur steht ganz oben,
  // die Basisspur darunter, der Ton unten. Im Schnitt selbst bleibt die
  // Reihenfolge, wie sie ist -- der Export zeichnet von unten nach oben.
  // (Sascha, 5. September: „Wir sollten die oberste Ebene oben haben.“)
  // Wer gerade welchen Clip in der Hand hat. Die Verbindung überträgt die
  // Auswahl der anderen längst; zu sehen war sie nirgends.
  const fremdeHand = useMemo(() => {
    const map = new Map<string, { name: string; color: string }>();
    for (const p of peers) for (const id of p.selection ?? []) map.set(id, { name: p.name, color: p.color });
    return map;
  }, [peers]);

  const angezeigteSpuren = useMemo(() => {
    const bild = timeline.tracks.filter((t) => t.kind === 'video');
    const rest = timeline.tracks.filter((t) => t.kind !== 'video');
    return [...[...bild].reverse(), ...rest];
  }, [timeline.tracks]);

  const spurenHoch = timeline.tracks.reduce((summe, t) => summe + hoeheVon(t.id, t.kind), 0);
  const hoehe = spurenHoch + 24 + (role !== 'viewer' ? 30 : 0);
  // Eingeklappt fällt die Zeile zum Anlegen neuer Spuren weg -- die braucht
  // man beim Platzsparen am wenigsten.
  const klein = spurenHoch + 23;

  return (
    <div
      className={`timeline-wrap ${eingeklappt ? 'eingeklappt' : ''}${platzierenAn ? ' platzieren' : ''}`}
      ref={wrapRef}
      data-spurhoehe-gezeigt={spurHoeheEff}
      data-platzieren={platzierenAn ? 'ja' : undefined}
    >
      {/* Der Griff an der Oberkante: er gehoert zu dem, was er groesser
          macht. */}
      <Griff />
      {umordnung ? <UmordnenReihe umordnung={umordnung} timeline={timeline} assetById={assetById} /> : null}

      {menu ? <ContextMenu x={menu.x} y={menu.y} items={menuItems(menu.clipId, menu.at)} onClose={() => setMenu(null)} /> : null}
      {trackMenu ? (
        <ContextMenu x={trackMenu.x} y={trackMenu.y} items={trackMenuItems(trackMenu.trackId)} onClose={() => setTrackMenu(null)} />
      ) : null}
      {leistenMenu ? (
        <ContextMenu
          x={leistenMenu.x}
          y={leistenMenu.y}
          items={leistenMenuItems(leistenMenu.at)}
          onClose={() => setLeistenMenu(null)}
        />
      ) : null}
      {zwischenMenu ? (
        <ContextMenu
          x={zwischenMenu.x}
          y={zwischenMenu.y}
          items={zwischenItems(zwischenMenu.trackId, zwischenMenu.raum)}
          onClose={() => setZwischenMenu(null)}
        />
      ) : null}
      {/* Eine Marke benennen -- dasselbe kleine Blatt wie beim Clip und bei der
          Spur. Leer lassen heisst: nur ein Strich an der Stelle. */}
      {markeName ? (
        <SpurName
          wert={markeName.name}
          kennung="diese Marke"
          titel="Marke benennen"
          platzhalter="z. B. Anmoderation, Applaus, Schnitt prüfen"
          hinweis="Leer lassen: nur ein Strich an der Stelle."
          onAbbrechen={() => setMarkeName(null)}
          onFertig={(name) => {
            dispatch({ op: 'updateMarke', markeId: markeName.markeId, patch: { label: name } } as Op);
            setMarkeName(null);
          }}
        />
      ) : null}
      {/* Umbenennen als eigenes kleines Blatt: ein Eingabefeld mitten in der
          Zeitleiste wäre auf dem Telefon unter der Tastatur verschwunden. */}
      {/* 7.14: Ein Clip heisst, was daraufsteht -- bisher hiess er, wie die
          Datei hiess. */}
      {clipName ? (
        <SpurName
          wert={clipName.name}
          kennung="diesen Clip"
          titel="Clip umbenennen"
          platzhalter="z. B. Anmoderation, B-Roll Bahnhof"
          hinweis="Leer lassen: wieder der Name des Mediums."
          onAbbrechen={() => setClipName(null)}
          onFertig={(name) => {
            dispatch({ op: 'updateClip', clipId: clipName.clipId, patch: { label: name } } as Op);
            setClipName(null);
          }}
        />
      ) : null}
      {angaben ? <ClipAngaben clipId={angaben} onClose={() => setAngaben(null)} /> : null}
      {umbenennen ? (
        <SpurName
          wert={umbenennen.name}
          kennung={umbenennen.trackId}
          onAbbrechen={() => setUmbenennen(null)}
          onFertig={(name) => {
            dispatch({ op: 'updateTrack', trackId: umbenennen.trackId, patch: { name } } as Op);
            setUmbenennen(null);
          }}
        />
      ) : null}
      <button
        type="button"
        className={`alles-zeigen ${zeigtAlles ? 'active' : ''}`}
        data-alles-zeigen={zeigtAlles ? 'an' : 'aus'}
        aria-label={zeigtAlles ? 'Zoom von vorher' : 'Ganzen Film zeigen'}
        title={zeigtAlles ? 'Zoom von vorher' : 'Ganzen Film zeigen'}
        onClick={allesZeigen}
        disabled={end <= 0}
      >
        {zeigtAlles ? 'Zurück' : 'Alles'}
      </button>
      <div
        className="timeline"
        ref={scroller}
        style={{ height: eingeklappt ? klein : hoehe }}
        onScroll={onScroll}
        onDragOver={ablageZiehen}
        onDrop={ablegen}
        data-zeitleiste-ablage
        // A touch on the cut while it plays is "stop here", the way it is in
        // every phone editor; otherwise the cut keeps sliding under the finger.
        onPointerDown={(e) => {
          if (useEditor.getState().playing) setPlaying(false);
          if (e.pointerType !== 'mouse' || e.button !== 0 || role === 'viewer') return;
          if ((e.target as Element).closest('.clip, .track-label, .add-tile, button, input, .ruler, [data-zwischen], [data-rand], [data-spur-plus]')) return;
          e.currentTarget.setPointerCapture(e.pointerId);
          setRahmen({ pointerId: e.pointerId, x0: e.clientX, y0: e.clientY, x: e.clientX, y: e.clientY, dazu: e.shiftKey || e.metaKey || e.ctrlKey });
        }}
        onPointerDownCapture={(e) => {
          // Ein Klick beim Überfahren behält die Stelle (7.207).
          if (e.pointerType === 'mouse') useEditor.getState().ueberfahrenEnde(true);
        }}
        onPointerLeave={(e) => {
          if (e.pointerType === 'mouse') useEditor.getState().ueberfahrenEnde(false);
        }}
        onPointerMove={(e) => {
          // **Vorschau beim Überfahren** (7.207; Sascha, 4. Oktober: „direkte
          // Vorschau bei mouse over 😎“): am Desktop zeigt die Maus über der
          // Zeitleiste das Bild an dieser Stelle; der Kopf kehrt zurück, wenn
          // sie geht. Nicht, solange eine Taste gedrückt ist oder etwas
          // gezogen wird, und nicht über den Spurnamen.
          if (
            frei &&
            e.pointerType === 'mouse' &&
            e.buttons === 0 &&
            !gesture.current &&
            !rahmen &&
            !(e.target as Element).closest('.track-label, .track-add')
          ) {
            useEditor.getState().ueberfahren(snapToFrame(Math.max(0, Math.min(end, timeAt(e.clientX))), fps));
          }
          if (rahmen && e.pointerId === rahmen.pointerId) {
            setRahmen({ ...rahmen, x: e.clientX, y: e.clientY });
            return;
          }
          onPointerMove(e);
        }}
        onPointerUp={(e) => {
          if (rahmen && e.pointerId === rahmen.pointerId) return rahmenEnde(e);
          endGesture(e);
        }}
        onPointerCancel={(e) => {
          if (rahmen && e.pointerId === rahmen.pointerId) return setRahmen(null);
          endGesture(e);
        }}
        onClick={(e) => {
          // Nach einem aufgezogenen Rahmen ist der Klick keiner ins Leere.
          if (rahmenGezogen.current) {
            rahmenGezogen.current = false;
            return;
          }
          // A tap on empty timeline clears the selection; taps on clips stop
          // propagation before they get here.
          if ((e.target as Element).closest('.clip, .track-label, .add-tile')) return;
          if (selection.length) select([]);
        }}
      >
        <div className="timeline-inner" style={{ width: contentWidth }}>
          {ablage ? (
            <div className="ablage-linie" data-ablage-linie={ablage.t.toFixed(3)} data-ablage-spur={ablage.spur ?? ''} style={{ left: centre + ablage.t * zoom }}>
              <span>{zeit(ablage.t)}</span>
            </div>
          ) : null}
          <Ruler
            end={end}
            zoom={zoom}
            centre={centre}
            tail={TAIL}
            kommentare={kommentare}
            onOpenKommentare={() => onOpenSheet('kommentare')}
            filmleiste={filmleiste}
            marken={timeline.marken}
            exportbereiche={timeline.exportbereiche}
            bereich={bereich}
            onMenu={(x, y, at) => setLeistenMenu({ x, y, at })}
            onOpenBereich={(id) => {
              useEditor.getState().setBereichBlatt(id);
              onOpenSheet('bereich');
            }}
            onBenenneAuswahl={benenneAuswahl}
          />

          {angezeigteSpuren.map((track) => (
            <div
              className={`track-row ${track.locked ? 'gesperrt' : ''} ${preview?.kollision ? 'kein-platz' : ''} ${preview && !preview.kollision && preview.trackId === track.id && preview.trackId !== dragSourceTrackId ? 'drop-target' : ''}`.replace(/\s+/g, ' ').trim()}
              key={track.id}
              data-track-id={track.id}
              style={{ height: hoeheVon(track.id, track.kind) }}
            >
              <div
                className="track-label"
                // The label takes a hold or a right click for the track menu;
                // everything else passes through to the scroller.
                onContextMenu={(e) => {
                  if (role === 'viewer') return;
                  e.preventDefault();
                  e.stopPropagation();
                  setTrackMenu({ trackId: track.id, x: e.clientX, y: e.clientY });
                }}
                onPointerDown={(e) => {
                  if (role === 'viewer' || (e.target as Element).closest('button')) return;
                  e.stopPropagation();
                  const { clientX, clientY, pointerId } = e;
                  if (labelPress.current) clearTimeout(labelPress.current.timer);
                  labelPress.current = {
                    pointerId,
                    trackId: track.id,
                    x: clientX,
                    y: clientY,
                    armed: false,
                    timer: setTimeout(() => {
                      if (labelPress.current?.pointerId === pointerId) labelPress.current.armed = true;
                    }, LONG_PRESS_MS),
                  };
                }}
                onPointerUp={(e) => {
                  const p = labelPress.current;
                  labelPress.current = null;
                  if (!p || p.pointerId !== e.pointerId) return;
                  clearTimeout(p.timer);
                  // A short tap on the label opens the menu too: it is the one
                  // thing a label is for, and a hold is not discoverable.
                  setTrackMenu({ trackId: p.trackId, x: p.x, y: p.y });
                }}
                onPointerCancel={() => {
                  if (labelPress.current) clearTimeout(labelPress.current.timer);
                  labelPress.current = null;
                }}
              >
                <span title={track.name ? `${track.name} (${track.id})` : track.id}>
                  {/* Der eigene Name, wenn es einen gibt -- „Kamera“ sagt mehr
                      als „V2“. Ohne Namen bleibt die Kennung stehen. */}
                  {track.name || track.id}
                  {track.duck ? <Icon name="music" size={10} /> : null}
                </span>
                <span className="track-schalter">
                {/* **Das Schloss steht am Spurkopf** (18. September, aus
                    Saschas Bildschirmfoto: dort trägt jede Spur Schloss und
                    Auge nebeneinander). Es lag bisher im Spur-Menü -- ein
                    Tipp, ein Menü, ein zweiter Tipp --, und dass eine Spur
                    gesperrt ist, sagte ein Emoji hinter dem Namen. Sperren ist
                    ein Handgriff, den man zwanzigmal am Tag macht: er gehört
                    neben das Auge. */}
                <button
                  className={track.locked ? 'off' : ''}
                  data-spur-schloss={track.locked ? 'zu' : 'offen'}
                  aria-label={track.locked ? `${track.id} entsperren` : `${track.id} sperren`}
                  title={track.locked ? `${track.id} entsperren` : `${track.id} sperren`}
                  disabled={role === 'viewer'}
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    dispatch({ op: 'updateTrack', trackId: track.id, patch: { locked: !track.locked } } as Op);
                  }}
                >
                  <Icon name={track.locked ? 'schloss' : 'schlossOffen'} size={14} />
                </button>
                {/* Muting a track and hiding one were in the data model from
                    the start and reachable from nowhere. On a phone they are
                    the quickest way to hear one thing at a time. */}
                <button
                  className={trackOff(track) ? 'off' : ''}
                  aria-label={trackSwitchLabel(track)}
                  title={trackSwitchLabel(track)}
                  disabled={role === 'viewer'}
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    dispatch(
                      track.kind === 'audio'
                        ? ({ op: 'updateTrack', trackId: track.id, patch: { muted: !track.muted } } as Op)
                        : ({ op: 'updateTrack', trackId: track.id, patch: { hidden: !track.hidden } } as Op),
                    );
                  }}
                >
                  <Icon
                    name={track.kind === 'audio' ? (track.muted ? 'mute' : 'volume') : track.hidden ? 'eyeOff' : 'eye'}
                    size={14}
                  />
                </button>
                </span>
              </div>
              {clipsOf(track).map((clip) => {
                const shown = preview?.clipId === clip.id ? preview.clip : clip;
                const asset = clip.source.kind === 'asset' ? assetById.get(clip.source.assetId) : undefined;
                return (
                  <ClipView
                    key={clip.id}
                    clip={shown}
                    track={track}
                    isBaseTrack={track.id === baseTrackId}
                    ziehend={preview?.clipId === clip.id}
                    onUebergang={
                      track.id === baseTrackId && uebergangsZiel(timeline, clip.start, [clip.id])?.clipId === clip.id
                        ? () => {
                            select([clip.id]);
                            onOpenSheet('uebergaenge');
                          }
                        : undefined
                    }
                    asset={asset}
                    zoom={zoom}
                    spurHoehe={spurHoehe}
                    tonkurve={track.kind === 'video' && tonkurven.includes(track.id)}
                    offset={centre}
                    selected={selection.includes(clip.id)}
                    pressing={pressing === clip.id}
                    fremd={fremdeHand.get(clip.id)}
                    hinweise={hinweiseJeClip.get(clip.id) ?? 0}
                    toggles={multiSelect}
                    onSelect={(dazu) => (multiSelect || dazu ? toggleSelect(clip.id) : select([clip.id]))}
                    mitgezogen={mitzieher?.has(clip.id) && clip.id !== preview?.clipId ? versatz : 0}
                    onDoppeltipp={
                      track.kind === 'video' && asset?.hasAudio ? () => tonkurveUmschalten(track.id) : undefined
                    }
                    onPointerDown={onClipPointerDown}
                    onContextMenu={(x, y) => {
                      if (role !== 'viewer') openMenu(clip.id, x, y);
                    }}
                    onJumpTo={(seconds) => setPlayhead(seconds)}
                    keyframeWahl={keyframeWahl?.clipId === clip.id ? keyframeWahl.at : null}
                    onWaehleKeyframe={(at) => waehleKeyframe({ clipId: clip.id, at })}
                    onBlende={
                      role !== 'viewer' && !track.locked
                        ? (patch) => dispatch({ op: 'updateClip', clipId: clip.id, patch } as Op)
                        : undefined
                    }
                  />
                );
              })}
              {/* 7.74d: an jedem Schnitt der Basisspur eine Marke, mittig auf
                  der Kante zwischen den beiden Clips. Sascha, 9. September:
                  „Wie bekomme ich die Übergänge in die Timeline? Ist irgendwie
                  unverständlich.“ Vorher musste man den Kopf an den Schnitt
                  stellen und wissen, dass es dort etwas gibt; jetzt steht dort
                  ein Pluszeichen -- und wo einer sitzt, seine Dauer. */}
              {/* 7.74f: auch auf den oberen Bildspuren. Dort lässt sich kein
                  Übergang rechnen -- aber „nichts zu sehen“ war die schlechtere
                  Antwort: Sascha, 11. September, „zwischen zwei Videos keinen
                  einfügen ... die Ui ist nicht rund“. Die Marke dort ist blass
                  und sagt beim Antippen, was gilt. */}
              {/* 7.74i: Das Pluszeichen steht nur da, wenn man Übergänge auch
                  meint. Sascha, 12. September: „Cool wäre, wenn die Übergangs
                  plus nur angezeigt werden, wenn ich im Menü auf übergang
                  drücke.“ Draussen bleibt die Zeitleiste ruhig -- sichtbar
                  bleibt nur, wo schon einer *sitzt*, denn das ist kein
                  Angebot, sondern eine Auskunft (und der Weg, ihn wieder
                  wegzunehmen).
                  Und: ein Pluszeichen heisst ab jetzt immer „hier geht
                  einer“. Auf einer oberen Spur steht ein Punkt, denn dort
                  geht keiner -- „wenn ich auf plus drücke lässt sich ein
                  Übergang nicht auswählen“ war genau das. */}
              {role !== 'viewer'
                ? uebergangsStellen(timeline)
                    .filter((stelle) => stelle.trackId === track.id)
                    .filter((stelle) => uebergangsModus || stelle.transition)
                    .map((stelle) => (
                    <button
                      key={`ueb-${stelle.clipId}`}
                      type="button"
                      // Seit dem 18. September trägt jeder Schnitt einen
                      // Übergang, auch auf einer oberen Spur -- die blasse
                      // Marke und ihr „geht hier nicht“ sind weg.
                      className={`uebergang-marke${stelle.transition ? ' gesetzt' : ''}${uebergangsModus ? ' gross' : ''}`}
                      style={{ left: centre + stelle.bei * zoom }}
                      data-uebergang-punkt={stelle.clipId}
                      aria-label={
                        stelle.transition
                          ? `Übergang zwischen „${stelle.vorLabel}“ und „${stelle.label}“: ${Math.round(stelle.transition.duration * 10) / 10} s`
                          : `Übergang zwischen „${stelle.vorLabel}“ und „${stelle.label}“ setzen`
                      }
                      title={
                        stelle.transition
                          ? `Übergang: ${Math.round(stelle.transition.duration * 10) / 10} s -- tippen zum Ändern oder Entfernen`
                          : 'Übergang an diesem Schnitt'
                      }
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        select([stelle.clipId]);
                        setPlayhead(stelle.bei);
                        onOpenSheet('uebergaenge');
                      }}
                    >
                      {stelle.transition ? `${Math.round(stelle.transition.duration * 10) / 10}s` : '+'}
                    </button>
                    ))
                : null}
              {/* **Das `+` in den Zwischenräumen** (7.215): an jedem Schnitt und
                  in jeder Lücke, auf jeder Spur -- ausser im Übergangs-Modus
                  und an einem Schnitt, an dem schon ein Übergang sitzt; dort
                  steht seine Marke. */}
              {role !== 'viewer' && !uebergangsModus && !track.locked
                ? zwischenraeume(track, fps)
                    // **Weniger Plus** (17. Oktober): an Schnitten erst, wenn
                    // etwas auf seine Stelle wartet. In Lücken immer -- dort
                    // steht auch „Lücke schließen“.
                    .filter((z) => z.art === 'luecke' || platzierenAn !== null)
                    .filter((z) => z.art === 'luecke' || !track.clips.find((c) => c.id === z.clipId)?.transitionIn)
                    // Nur wo Platz ist: das Plus liegt sonst auf einem schmalen
                    // Clip und schluckt den Tipp darauf -- bei kleinem Zoom
                    // fing es so den Griff nach einem Markenclip ab.
                    .filter((z) => {
                      if (z.art === 'luecke') return (z.bis - z.von) * zoom >= 32;
                      const breite = (id: string) => (track.clips.find((c) => c.id === id)?.duration ?? 0) * zoom;
                      return breite(z.vorClipId) >= 48 && breite(z.clipId) >= 48;
                    })
                    .map((z) => {
                      const bei = z.art === 'schnitt' ? z.bei : (z.von + z.bis) / 2;
                      return (
                        <button
                          key={`zw-${z.art}-${bei.toFixed(3)}`}
                          type="button"
                          className={`zwischen-plus ${z.art}${z.art === 'schnitt' && (selection.includes(z.clipId) || selection.includes(z.vorClipId)) ? ' oben' : ''}`}
                          style={{ left: centre + plusLage(z) * zoom }}
                          data-zwischen={z.art}
                          data-zwischen-bei={bei.toFixed(3)}
                          aria-label={z.art === 'schnitt' ? 'Am Schnitt: Übergang oder einfügen' : 'In der Lücke: einfügen oder schließen'}
                          onPointerDown={(e) => e.stopPropagation()}
                          onClick={(e) => {
                            e.stopPropagation();
                            // Wartet etwas auf seine Stelle (17. Oktober), kommt es
                            // gleich hierher: am Schnitt, in der Lücke an ihren Anfang.
                            if (useEditor.getState().platzieren) platziere(z.art === 'schnitt' ? z.bei : z.von, track.id);
                            else setZwischenMenu({ x: e.clientX, y: e.clientY, trackId: track.id, raum: z });
                          }}
                        >
                          <Icon name="plus" size={14} />
                        </button>
                      );
                    })
                : null}
              {/* **Vor und hinter den Clips** (7.233): am Anfang der Spur, wenn
                  der erste Clip bei 0 beginnt, und hinter dem letzten. Auf der
                  Hauptspur steht hinten schon die Kachel zum Anfügen. Ein Tipp
                  fügt gleich dort ein -- es gibt nur eine Wahl. */}
              {role !== 'viewer' && !uebergangsModus && !track.locked && platzierenAn
                ? randStellen(track, fps)
                    .filter((r) => !(r.art === 'ende' && track.id === baseTrackId))
                    .filter((r) => (track.clips.find((c) => c.id === r.clipId)?.duration ?? 0) * zoom >= 48)
                    .map((r) => (
                      <button
                        key={`rand-${r.art}`}
                        type="button"
                        className={`zwischen-plus rand ${r.art}${selection.includes(r.clipId) ? ' oben' : ''}`}
                        style={{ left: centre + r.bei * zoom }}
                        data-rand={r.art}
                        data-rand-bei={r.bei.toFixed(3)}
                        aria-label={r.art === 'anfang' ? 'Davor einfügen, am Anfang der Spur' : 'Dahinter einfügen'}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (useEditor.getState().platzieren) {
                            platziere(r.bei, track.id);
                            return;
                          }
                          const b = snapToFrame(r.bei, fps);
                          setPlayhead(b);
                          setEinfuegeStelle(b);
                          useEditor.getState().setEinfuegeSpur(track.id);
                          onAdd();
                        }}
                      >
                        <Icon name="plus" size={14} />
                      </button>
                    ))
                : null}
              {platzierenAn && spurPlus.has(track.id) ? (
                <button
                  type="button"
                  className="zwischen-plus spur-plus"
                  style={{ left: `calc(${centre}px + var(--kopf-x, 0px))` }}
                  data-spur-plus={track.id}
                  aria-label={`Hier einfügen, auf ${track.name || track.id}`}
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (useEditor.getState().platzieren) {
                      platziere(useEditor.getState().playhead, track.id);
                      return;
                    }
                    const b = snapToFrame(useEditor.getState().playhead, fps);
                    setPlayhead(b);
                    setEinfuegeStelle(b);
                    useEditor.getState().setEinfuegeSpur(track.id);
                    onAdd();
                  }}
                >
                  <Icon name="plus" size={14} />
                </button>
              ) : null}
              {track.id === baseTrackId && role !== 'viewer' ? (
                <button
                  className="add-tile"
                  style={{ left: centre + end * zoom + (end > 0 ? 20 : 12) }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (useEditor.getState().platzieren) {
                      platziere(end, track.id);
                      return;
                    }
                    setEinfuegeStelle(null);
                    onAdd();
                  }}
                  data-add-tile
                  aria-label="Medien hinzufügen"
                  title="Hinten anfügen"
                >
                  <Icon name="plus" size={14} />
                </button>
              ) : null}
            </div>
          ))}

          {role !== 'viewer' ? (
            <div className="track-add">
              <button onClick={(e) => { e.stopPropagation(); addTrack('video'); }} aria-label="Bildspur hinzufügen">
                <Icon name="plus" size={14} /> Bildspur
              </button>
              <button onClick={(e) => { e.stopPropagation(); addTrack('audio'); }} aria-label="Tonspur hinzufügen">
                <Icon name="plus" size={14} /> Tonspur
              </button>
            </div>
          ) : null}

          {/* Wo die anderen gerade hinschauen. Der Strich allein sagt nur,
              *dass* jemand da ist -- das Fähnchen sagt, wer. Auf dem Telefon
              gibt es keinen Mauszeiger, unter dem ein Titel erscheint. */}
          {peers.map((p, i) => (
            <div
              key={`${p.userId}-${i}`}
              className="peer-playhead"
              style={{ left: centre + p.playhead * zoom, background: p.color }}
              title={p.selbst ? 'Du selbst, in einem anderen Fenster' : `${p.name} ist hier`}
              data-selbst={p.selbst ? 'ja' : undefined}
              data-peer-at={p.playhead.toFixed(2)}
            >
              <span className="fahne" style={{ background: p.color }}>
                {initialen(p.name)}
              </span>
            </div>
          ))}
        </div>
      </div>
      {rahmen && Math.hypot(rahmen.x - rahmen.x0, rahmen.y - rahmen.y0) >= 4 ? (
        <div
          className="auswahl-rahmen"
          data-auswahl-rahmen
          aria-hidden="true"
          style={{
            left: Math.min(rahmen.x0, rahmen.x),
            top: Math.min(rahmen.y0, rahmen.y),
            width: Math.abs(rahmen.x - rahmen.x0),
            height: Math.abs(rahmen.y - rahmen.y0),
          }}
        />
      ) : null}
      <div
        className={`playhead ${frei ? 'frei' : ''}`.trim()}
        aria-hidden="true"
        data-kopf={frei ? 'frei' : 'fest'}
        style={frei ? { left: `calc(${centre}px + var(--kopf-x, 0px) - var(--roll-x, 0px))` } : undefined}
      />
      {frei && ueberfaehrt ? (
        <div className="skimmer" data-skimmer aria-hidden="true" style={{ left: `calc(${centre}px + var(--skim-x, 0px) - var(--roll-x, 0px))` }} />
      ) : null}
      {/* Am Desktop zum Greifen (7.206): oben am Dreieck, über dem Lineal --
          weiter unten liegen die Clips, und die will man dort anfassen. */}
      {frei ? (
        <div
          className="kopf-griff"
          data-kopf-griff
          title="Abspielkopf ziehen"
          style={{ left: `calc(${centre}px + var(--kopf-x, 0px) - var(--roll-x, 0px))` }}
          onPointerDown={kopfGreifen}
        />
      ) : null}
      {/* Das `+` auf der Abspiellinie ist weg (7.216; Sascha, 5. Oktober:
          „kann weg“). Eingefügt wird über die Pluszeichen in den Schnitten
          und Lücken jeder Spur (7.215), in freien Zeilen unter der Linie
          (7.229) und hinten am Ende der Hauptspur. */}
    </div>
  );
}

/** A track that is muted or hidden is "off", whichever kind it is. */
/**
 * Die Hauptspur als Reihe gleich grosser Miniaturen, solange umgeordnet wird
 * (15. Oktober). Die gezogene folgt dem Finger; die anderen rücken auf ihre
 * Plätze, und an der Stelle, an der sie landen würde, bleibt eine frei.
 */
function UmordnenReihe({
  umordnung,
  timeline,
  assetById,
}: {
  umordnung: { clipId: string; x: number; anfang: number; mitte: number };
  timeline: { tracks: Track[] };
  assetById: Map<string, AssetView>;
}): ReactElement {
  const basis = timeline.tracks.find((t) => t.kind === 'video');
  const reihe = basis ? sortedClips(basis) : [];
  const ziel = umordnenZiel(umordnung.x, umordnung.anfang, UMORDNEN_SCHRITT, UMORDNEN_KACHEL, reihe.length);
  const andere = reihe.filter((c) => c.id !== umordnung.clipId);
  const oben = umordnung.mitte - UMORDNEN_KACHEL / 2;
  const kachel = (c: Clip, links: number, platz: number, gezogen: boolean) => {
    const asset = c.source.kind === 'asset' ? assetById.get(c.source.assetId) : undefined;
    const bild = asset?.posterUrl ?? null;
    return (
      <div
        key={c.id}
        className={`umordnen-kachel${gezogen ? ' gezogen' : ''}`}
        data-umordnen-kachel={c.id}
        data-platz={platz}
        style={{
          left: links,
          top: oben,
          ...(bild ? { backgroundImage: `url(${JSON.stringify(bild).slice(1, -1)})` } : {}),
          ...(c.source.kind === 'color' ? { background: c.source.color } : {}),
        }}
      >
        {bild || c.source.kind === 'color' ? null : <span>{(c.label || asset?.name || '?').slice(0, 2)}</span>}
      </div>
    );
  };
  return (
    <div className="umordnen" data-umordnen data-umordnen-ziel={ziel} aria-hidden="true">
      <div className="umordnen-hinweis">Ziehen zum Umordnen</div>
      {andere.map((c, i) => {
        const platz = i >= ziel ? i + 1 : i;
        return kachel(c, umordnung.anfang + platz * UMORDNEN_SCHRITT, platz, false);
      })}
      {reihe
        .filter((c) => c.id === umordnung.clipId)
        .map((c) => kachel(c, umordnung.x - UMORDNEN_KACHEL / 2, ziel, true))}
    </div>
  );
}

function trackOff(track: Track): boolean {
  return track.kind === 'audio' ? track.muted : track.hidden;
}

function trackSwitchLabel(track: Track): string {
  if (track.kind === 'audio') return track.muted ? `${track.id} wieder hörbar machen` : `${track.id} stummschalten`;
  return track.hidden ? `${track.id} wieder einblenden` : `${track.id} ausblenden`;
}

function Ruler({
  end,
  zoom,
  centre,
  tail,
  kommentare,
  onOpenKommentare,
  filmleiste,
  marken,
  bereich,
  exportbereiche,
  onMenu,
  onOpenBereich,
  onBenenneAuswahl,
}: {
  end: number;
  zoom: number;
  centre: number;
  tail: number;
  kommentare: KommentarView[];
  onOpenKommentare: () => void;
  filmleiste: { von: number; bis: number; stand: 'fertig' | 'rechnet' | 'fehlt' }[];
  /** Benannte Stellen (S5). */
  marken: readonly Marke[];
  /** Der gewählte Bereich (S5), oder `null` für den ganzen Schnitt. */
  bereich: { von: number; bis: number } | null;
  /** Benannte Bereiche, aus denen eigene Filme werden (S33). */
  exportbereiche: readonly Exportbereich[];
  /** Ein langer Druck oder ein Rechtsklick auf die Leiste, mit der Stelle. */
  onMenu: (x: number, y: number, at: number) => void;
  /** Ein Tipp auf den Namen eines Bereichs öffnet sein Blatt (7.166). */
  onOpenBereich: (id: string) => void;
  /** Ein Tipp auf „Benennen“ in der Auswahl macht daraus einen Bereich (7.197). */
  onBenenneAuswahl: () => void;
}): ReactElement {
  const zeit = useZeitText();
  const setPlayhead = useEditor((s) => s.setPlayhead);
  const dispatch = useEditor((s) => s.dispatch);
  const rolle = useEditor((s) => s.role);
  /**
   * Eine Kante eines Exportbereichs am Finger (S33).
   *
   * Bis zum 20. September liessen sich die Grenzen nur über „neu anlegen“
   * ändern -- man musste den Bereich wählen, neu setzen und den Namen wieder
   * tippen, um eine Sekunde zu verschieben. Das stand als „was fehlt“ am
   * Punkt selbst, und Saschas Satz dazu war „Bereiche müssen besser
   * erreichbar sein“.
   *
   * Geschickt wird erst beim Loslassen: ein Zug ist eine Bewegung und nicht
   * sechzig Schritte, und sechzig Schritte wären sechzig Zeilen im Verlauf,
   * die niemand einzeln zurücknehmen will.
   */
  const [kanteZieht, setKanteZieht] = useState<{ id: string; kante: 'von' | 'bis'; von: number; bis: number } | null>(null);
  const kante = useRef<{ pointerId: number; id: string; kante: 'von' | 'bis'; von: number; bis: number } | null>(null);
  const leiste = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    /** Ein Bereich unter einer Zehntelsekunde ist keiner mehr. */
    const MINDESTENS = 0.1;
    const bewegt = (event: PointerEvent): void => {
      const k = kante.current;
      if (!k || event.pointerId !== k.pointerId) return;
      const box = leiste.current?.getBoundingClientRect();
      if (!box) return;
      const at = Math.max(0, (event.clientX - box.left - centre) / zoom);
      const naechster =
        k.kante === 'von'
          ? { ...k, von: Math.min(at, k.bis - MINDESTENS) }
          : { ...k, bis: Math.max(at, k.von + MINDESTENS) };
      kante.current = naechster;
      setKanteZieht({ id: naechster.id, kante: naechster.kante, von: naechster.von, bis: naechster.bis });
    };
    const los = (event: PointerEvent): void => {
      const k = kante.current;
      if (!k || event.pointerId !== k.pointerId) return;
      kante.current = null;
      setKanteZieht(null);
      const patch = k.kante === 'von' ? { von: k.von } : { bis: k.bis };
      dispatch({ op: 'updateExportbereich', bereichId: k.id, patch } as Op);
    };
    window.addEventListener('pointermove', bewegt);
    window.addEventListener('pointerup', los);
    window.addEventListener('pointercancel', los);
    return () => {
      window.removeEventListener('pointermove', bewegt);
      window.removeEventListener('pointerup', los);
      window.removeEventListener('pointercancel', los);
    };
  }, [centre, zoom, dispatch]);
  // Wo der Kopf steht, als Zahl an der Leiste. Die Anzeige in der
  // Transportleiste ist umschaltbar (S31) und taugt nicht zum Nachrechnen --
  // hier steht die Sekunde, unabhängig vom gewählten Format.
  const kopf = useEditor((s) => s.playhead);
  const leistenDruck = useRef<{ pointerId: number; armed: boolean; timer: ReturnType<typeof setTimeout> } | null>(null);
  // Aim for a label roughly every 80 px, on a round number of seconds.
  const candidates = [0.5, 1, 2, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1800];
  const step = candidates.find((c) => c * zoom >= 80) ?? 3600;
  const ticks: number[] = [];
  for (let t = 0; t <= end + tail / zoom; t += step) ticks.push(t);

  return (
    <div
      className="ruler"
      ref={leiste}
      data-bereich={bereich ? `${bereich.von.toFixed(3)}-${bereich.bis.toFixed(3)}` : undefined}
      data-kopf={kopf.toFixed(3)}
      onClick={(e) => {
        // A tap on the ruler jumps there -- the one place a tap moves the
        // playhead instead of a scroll, for laptops without a trackpad flick.
        if (leistenDruck.current?.armed) return;
        const box = e.currentTarget.getBoundingClientRect();
        const t = (e.clientX - box.left - centre) / zoom;
        setPlayhead(Math.max(0, Math.min(end, t)));
      }}
      // Ein langer Druck auf die Leiste öffnet, was zu *dieser Stelle* gehört
      // (S5): Marke setzen, Bereich anfangen, Bereich beenden.
      onPointerDown={(e) => {
        // Nur die primäre Taste und der Finger. Ein Rechtsklick löst ebenfalls
        // `pointerdown` aus; ohne diese Zeile bewaffnet er den langen Druck
        // mit, und eine halbe Sekunde nach dem Menü öffnet sich ein zweites
        // über allem, was inzwischen davor steht.
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        const box = e.currentTarget.getBoundingClientRect();
        const at = (e.clientX - box.left - centre) / zoom;
        const { clientX, clientY, pointerId } = e;
        if (leistenDruck.current) clearTimeout(leistenDruck.current.timer);
        leistenDruck.current = {
          pointerId,
          armed: false,
          timer: setTimeout(() => {
            if (leistenDruck.current?.pointerId !== pointerId) return;
            leistenDruck.current.armed = true;
            navigator.vibrate?.(8);
            onMenu(clientX, clientY, at);
          }, LONG_PRESS_MS),
        };
      }}
      onPointerUp={() => {
        if (leistenDruck.current) clearTimeout(leistenDruck.current.timer);
        // `armed` bleibt für den Klick stehen, der gleich folgt, und wird
        // danach im selben Zug vergessen.
        const war = leistenDruck.current?.armed ?? false;
        if (!war) leistenDruck.current = null;
        else setTimeout(() => { leistenDruck.current = null; }, 0);
      }}
      onPointerCancel={() => {
        if (leistenDruck.current) clearTimeout(leistenDruck.current.timer);
        leistenDruck.current = null;
      }}
      // Zwei Finger zoomen (7.218) -- sie halten nicht lange gedrückt. Ohne
      // das ging beim Zoomen über der Leiste ihr Menü auf.
      onTouchStart={(e) => {
        if (e.touches.length < 2) return;
        if (leistenDruck.current) clearTimeout(leistenDruck.current.timer);
        leistenDruck.current = null;
      }}
      onContextMenu={(e) => {
        // Am Schreibtisch die rechte Maustaste -- dort ist der lange Druck
        // ungewohnt und der Rechtsklick das, was jeder probiert.
        e.preventDefault();
        const box = e.currentTarget.getBoundingClientRect();
        onMenu(e.clientX, e.clientY, (e.clientX - box.left - centre) / zoom);
      }}
    >
      {/* Die benannten Bereiche (S33): ein Band je Bereich, mit seinem Namen
          darin. Sie liegen *unter* dem gewählten Bereich, weil der die
          Absicht für den nächsten Knopfdruck ist und obenauf gehört -- und
          sie sind blasser, weil sie immer dastehen und nicht schreien
          sollen. */}
      {exportbereiche.map((roh) => {
        // Während eines Zugs zeigt das Band, wo der Finger steht, und nicht,
        // was auf dem Server liegt: sonst klebte die Kante am alten Wert, bis
        // die Antwort zurück ist.
        const b = kanteZieht?.id === roh.id ? { ...roh, von: kanteZieht.von, bis: kanteZieht.bis } : roh;
        const greif = (welche: 'von' | 'bis') => (event: React.PointerEvent) => {
          if (rolle === 'viewer') return;
          event.stopPropagation();
          event.preventDefault();
          (event.target as Element).setPointerCapture?.(event.pointerId);
          kante.current = { pointerId: event.pointerId, id: b.id, kante: welche, von: b.von, bis: b.bis };
          setKanteZieht({ id: b.id, kante: welche, von: b.von, bis: b.bis });
        };
        // Die Farbe des Bereichs (S42) steht als eine Zahl am Element und
        // wird im Stilblatt gemischt -- so bleibt „blasser als der gewählte
        // Bereich“ an einer Stelle und nicht in sechs.
        return (
        <span
          key={b.id}
          className="exportbereich-band"
          data-exportbereich={b.id}
          data-exportbereich-name={b.name}
          data-exportbereich-von={b.von.toFixed(3)}
          data-exportbereich-bis={b.bis.toFixed(3)}
          data-exportbereich-farbe={b.farbe}
          title={`${b.name} — ${zeit(b.von)} bis ${zeit(b.bis)}`}
          style={{
            left: centre + b.von * zoom,
            width: Math.max(2, (b.bis - b.von) * zoom),
            ['--bereich' as string]: BEREICH_FARBWERTE[b.farbe],
          }}
        >
          {/* Der Name ist ein Knopf (7.166): ein Tipp öffnet das Blatt mit
              Name, Farbe und Anheften. Er fängt Druck und Tipp selbst, damit
              die Leiste darunter weder den Kopf setzt noch ihr Menü öffnet.
              Die Nadel davor sagt, dass er an seinen Clips hängt. */}
          <button
            type="button"
            className="exportbereich-text"
            data-exportbereich-oeffnen={b.id}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onOpenBereich(b.id);
            }}
          >
            {b.angeheftet ? <span className="exportbereich-nadel" data-exportbereich-angeheftet={b.id} aria-label="angeheftet">📌</span> : null}
            {b.name}
          </button>
          {/* Die Kanten zum Ziehen (S33). Sie greifen über das Band hinaus,
              weil ein Band von zwei Sekunden bei kleinem Zoom schmaler ist
              als ein Daumen -- gegriffen wird die Kante, nicht das Band. */}
          {rolle !== 'viewer' ? (
            <>
              {/* Der Tipp bleibt hier: ein Zug an der Kante darf den
                  Abspielkopf nicht mitnehmen. */}
              <span
                className="exportbereich-kante links"
                data-exportbereich-kante={`${b.id}:von`}
                onPointerDown={greif('von')}
                onClick={(e) => e.stopPropagation()}
              />
              <span
                className="exportbereich-kante rechts"
                data-exportbereich-kante={`${b.id}:bis`}
                onPointerDown={greif('bis')}
                onClick={(e) => e.stopPropagation()}
              />
            </>
          ) : null}
        </span>
        );
      })}
      {/* Der gewählte Bereich (S5): ein Band über der Leiste, damit man sieht,
          was ein Export treffen würde. */}
      {/* Und sie ist anfassbar (7.197; Sascha, 4. Oktober, mit Bildschirmfoto:
          „kann leider nur einen blauen Bereich erstellen, nicht klicken“).
          Ein Tipp auf „Benennen“ macht aus der Auswahl einen benannten
          Bereich und öffnet sein Blatt: Name, Art, Anheften. Ist sie schon
          einer, steht dort sein Name, und der öffnet ihn -- das Band selbst
          lässt den Tipp weiter durch, weil er den Abspielkopf setzt. */}
      {bereich ? (
        <span
          className="bereich-band"
          data-auswahl-band
          style={{ left: centre + bereich.von * zoom, width: Math.max(2, (bereich.bis - bereich.von) * zoom) }}
        >
          {rolle !== 'viewer' && !exportbereiche.some((b) => Math.abs(b.von - bereich.von) < 0.02 && Math.abs(b.bis - bereich.bis) < 0.02) ? (
            <button
              type="button"
              className="bereich-benennen"
              data-bereich-benennen
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                onBenenneAuswahl();
              }}
            >
              Benennen…
            </button>
          ) : null}
        </span>
      ) : null}
      {/* Benannte Stellen (S5). Ein Tipp springt hin; ein langer Druck öffnet
          dasselbe Menü wie die Leiste darunter, dort steht dann „Marke
          wegnehmen“ und „Marke benennen“. */}
      {marken.map((marke) => (
        <button
          key={marke.id}
          className="marke"
          data-marke-id={marke.id}
          data-marke-at={marke.at}
          style={{ left: centre + marke.at * zoom }}
          title={marke.label || zeit(marke.at)}
          onClick={(e) => {
            e.stopPropagation();
            setPlayhead(marke.at);
          }}
          onContextMenu={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onMenu(e.clientX, e.clientY, marke.at);
          }}
        >
          <span className="marke-fahne" aria-hidden="true" />
          {marke.label ? <span className="marke-text">{marke.label}</span> : null}
        </button>
      ))}
      {/* Was von der Vorschau gerechnet ist (7.71b) -- nicht mehr zu sehen
          (Sascha, 3. Oktober: „was bringt er? Entfernen!“), aber noch im
          Dokument: Prüfstand und Diagnose lesen daraus, was fertig ist. */}
      {filmleiste.length > 0 ? (
        <div className="filmleiste" hidden aria-hidden="true" data-filmleiste={filmleiste.filter((a) => a.stand === 'fertig').length}>
          {filmleiste.map((a) => (
            <span
              key={`${a.von}-${a.bis}`}
              className={`film-stueck ${a.stand}`}
              data-stand={a.stand}
              style={{ left: centre + a.von * zoom, width: Math.max(1, (a.bis - a.von) * zoom) }}
            />
          ))}
        </div>
      ) : null}
      {/* Hinweise sitzen auf der Leiste, nicht auf einem Clip: sie meinen eine
          Stelle im Film, und die bleibt, auch wenn der Clip darunter geteilt
          oder ersetzt wird. Ein Tipp springt hin und öffnet die Liste. */}
      {kommentare
        .filter((k) => !k.parent_id && !k.resolved_at)
        .map((k) => (
          <button
            key={k.id}
            className="kommentar-marke"
            data-kommentar-at={k.at_seconds}
            style={{ left: centre + k.at_seconds * zoom }}
            title={`${k.author_name}: ${k.body.slice(0, 80)}`}
            onClick={(e) => {
              e.stopPropagation();
              setPlayhead(k.at_seconds);
              onOpenKommentare();
            }}
          >
            <Icon name="captions" size={11} />
          </button>
        ))}
      {ticks.map((t) => (
        <div className="tick" key={t} style={{ left: centre + t * zoom }}>
          {zeit(t)}
        </div>
      ))}
    </div>
  );
}

/**
 * Doppeltipp auf einen Clip (Sascha, 8. September: „Audio ausklappen
 * koennten wir mit doppeltipp/click machen“). Der erste Tipp waehlt, wie
 * bisher; kommt der zweite innerhalb dieser Frist auf denselben Clip, klappt
 * die Tonkurve auf oder zu. 350 ms ist, was die Systeme selbst als
 * Doppeltipp zaehlen.
 */
const DOPPELTIPP_MS = 350;
let letzterTipp: { id: string; t: number } | null = null;

/**
 * So weit steht ein Blendengriff mindestens vom Rand des Clips (7.208): an
 * der Kante sitzt oben das Plus des Schnitts (7.215) und darüber läge der
 * Griff darunter. Die Rampe zeigt die wahre Länge, auch wenn sie kürzer ist.
 * 26 und nicht mehr 16, seit das Plus 24 px misst und 8 px Trefffläche
 * drumherum hat (7.235): 12 + 8 = 20 px reicht es in den Clip hinein.
 */
const BLENDE_RAND = 26;

/** Wo die Null mit freiem Kopf steht (7.206): hinter den Spurnamen. */
const FREI_LINKS = 140;

function ClipView({
  clip,
  track,
  isBaseTrack,
  asset,
  zoom,
  offset,
  spurHoehe,
  tonkurve = false,
  selected,
  pressing,
  fremd,
  toggles,
  onSelect,
  onDoppeltipp,
  onPointerDown,
  onContextMenu,
  onJumpTo,
  keyframeWahl,
  onWaehleKeyframe,
  onUebergang,
  hinweise = 0,
  ziehend = false,
  onBlende,
  mitgezogen = 0,
}: {
  clip: Clip;
  track: Track;
  isBaseTrack: boolean;
  /** Offene Hinweise an diesem Clip -- die Marke am Objekt. */
  hinweise?: number;
  /** Gerade in der Hand (7.18): dann folgt der Clip dem Finger ohne Übergang; losgelassen rastet er ein. */
  ziehend?: boolean;
  /** Es gibt einen Clip davor: der Punkt an der Schnittkante öffnet das Blatt „Übergänge“ (7.74c). */
  onUebergang?: () => void;
  asset: AssetView | undefined;
  zoom: number;
  offset: number;
  /** Wie hoch die Spur steht -- der Filmstreifen muss davon wissen. */
  spurHoehe: number;
  /** Ist die Tonkurve dieser Bildspur aufgeklappt? (7.65) */
  tonkurve?: boolean;
  selected: boolean;
  pressing: boolean;
  /** Hält jemand anderes diesen Clip gerade? Dann trägt er dessen Farbe. */
  fremd: { name: string; color: string } | undefined;
  /** In multi-select a tap on a selected clip removes it from the selection. */
  toggles: boolean;
  /** `dazu`: mit Umschalt/Befehl/Strg geklickt -- zur Auswahl dazu oder heraus (7.205). */
  onSelect: (dazu: boolean) => void;
  /** Zieht mit einer Auswahl mit (7.205): so viele Pixel neben seinem Platz. */
  mitgezogen?: number;
  /** Zweimal kurz hintereinander: die Tonkurve auf und zu (7.65b). */
  onDoppeltipp?: () => void;
  onPointerDown: (event: React.PointerEvent, kind: GestureKind, clip: Clip, trackId: string, keyframeAt?: number, pegelGain?: number, clipHoehe?: number) => void;
  onContextMenu: (x: number, y: number) => void;
  onJumpTo: (seconds: number) => void;
  /** Welcher Keyframe dieses Clips angewählt ist -- oder null. */
  keyframeWahl: number | null;
  onWaehleKeyframe: (at: number) => void;
  /** Ein- und Ausblenden am Clip ziehen (7.208) -- ohne: nur ansehen. */
  onBlende?: (patch: { fadeIn?: number; fadeOut?: number }) => void;
}): ReactElement {
  const width = Math.max(14, clip.duration * zoom);
  // **Die Blenden am Clip** (7.208; Sascha, 4. Oktober). Während des Zugs
  // gilt der Wert in der Hand; geschickt wird beim Loslassen, ein Schritt
  // für „Rückgängig“ statt sechzig.
  const [blendeZug, setBlendeZug] = useState<{ art: 'ein' | 'aus'; wert: number } | null>(null);
  const einblenden = blendeZug?.art === 'ein' ? blendeZug.wert : clip.fadeIn;
  const ausblenden = blendeZug?.art === 'aus' ? blendeZug.wert : clip.fadeOut;
  const blendeGriff = (art: 'ein' | 'aus') => (e: React.PointerEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    if (!onBlende) return;
    const griff = e.currentTarget;
    griff.setPointerCapture(e.pointerId);
    const x0 = e.clientX;
    const anfang = art === 'ein' ? clip.fadeIn : clip.fadeOut;
    // Beide Blenden zusammen nie länger als der Clip, und höchstens zehn
    // Sekunden -- so weit lässt das Schema sie zu.
    const hoechstens = Math.max(0, Math.min(10, clip.duration - (art === 'ein' ? clip.fadeOut : clip.fadeIn)));
    let wert = anfang;
    const zug = (ev: PointerEvent) => {
      const dx = (ev.clientX - x0) / Math.max(zoom, 1e-6);
      wert = Math.max(0, Math.min(hoechstens, Math.round((anfang + (art === 'ein' ? dx : -dx)) * 10) / 10));
      setBlendeZug({ art, wert });
    };
    const los = () => {
      griff.removeEventListener('pointermove', zug);
      griff.removeEventListener('pointerup', los);
      griff.removeEventListener('pointercancel', los);
      setBlendeZug(null);
      if (Math.abs(wert - anfang) > 1e-9) onBlende(art === 'ein' ? { fadeIn: wert } : { fadeOut: wert });
    };
    griff.addEventListener('pointermove', zug);
    griff.addEventListener('pointerup', los);
    griff.addEventListener('pointercancel', los);
  };
  const kindClass =
    clip.source.kind === 'text'
      ? 'text-clip'
      : clip.source.kind === 'brand'
        ? 'brand-clip'
        : clip.source.kind === 'color'
          ? 'color-clip'
          : // Ton bekommt seine eigene Farbe; eine Sprachaufnahme aus dem
            // Voiceover eine andere als Musik, weil man die beiden im Schnitt
            // ständig auseinanderhalten muss.
            track.kind === 'audio' || asset?.kind === 'audio'
            ? `audio-clip${/voice|voiceover|sprach/i.test(clip.label || asset?.name || '') ? ' voice' : ''}`
            : '';

  const label =
    clip.label ||
    (clip.source.kind === 'text'
      ? clip.source.text.split('\n')[0]
      : clip.source.kind === 'brand'
        ? 'Marke'
        : clip.source.kind === 'color'
          ? 'Farbfläche'
          : (asset?.name ?? 'Clip'));

  return (
    <div
      className={`clip ${kindClass} ${selected ? 'selected' : ''} ${clip.muted ? 'muted' : ''} ${pressing ? 'pressing' : ''} ${ziehend ? 'zieht' : ''} ${fremd ? 'fremd' : ''} ${tonkurve ? 'mit-tonbahn' : ''} ${clip.gruppe ? 'gruppiert' : ''}`.replace(/\s+/g, ' ').trim()}
      data-gruppe={clip.gruppe ?? undefined}
      style={{
        ...(fremd ? { outlineColor: fremd.color } : {}),
        left: offset + clip.start * zoom,
        width,
        ...(mitgezogen ? { transform: `translateX(${mitgezogen}px)`, transition: 'none' } : {}),
        ...(tonkurve ? ({ '--tonbahn': `${TONKURVE_HOEHE}px` } as Record<string, string>) : {}),
      }}
      data-clip-id={clip.id}
      onPointerDown={(e) => onPointerDown(e, 'move', clip, track.id)}
      // A right click on a laptop; iOS never sends this for a held finger,
      // which is why the timeline times the hold itself.
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onContextMenu(e.clientX, e.clientY);
      }}
      onClick={(e) => {
        e.stopPropagation();
        // Der Doppeltipp wird selbst gezaehlt: `dblclick` kommt auf dem
        // Telefon nicht verlaesslich an, und `e.detail` auch nicht.
        const jetzt = performance.now();
        const vorher = letzterTipp;
        letzterTipp = { id: clip.id, t: jetzt };
        if (onDoppeltipp && vorher && vorher.id === clip.id && jetzt - vorher.t < DOPPELTIPP_MS) {
          letzterTipp = null;
          onDoppeltipp();
          return;
        }
        const dazu = e.shiftKey || e.metaKey || e.ctrlKey;
        if (dazu || !selected || toggles) onSelect(dazu);
      }}
    >
      {asset?.filmstripUrl && track.kind === 'video' ? (
        <Filmstrip url={asset.filmstripUrl} clip={clip} asset={asset} width={width} hoehe={spurHoehe} />
      ) : null}
      {/* Ein Standbild hat keinen Filmstreifen -- es gibt ja nur ein Bild. Ohne
          das hier bleibt der Clip ein grauer Kasten mit einem Dateinamen
          darauf, und im Schnitt sieht das aus wie eine Leiche. Das Bild
          wiederholt sich in seinem eigenen Seitenverhältnis über die Breite,
          wie die Einzelbilder eines Filmstreifens, statt gezerrt zu werden. */}
      {!asset?.filmstripUrl && asset?.kind === 'image' && asset.posterUrl && track.kind === 'video' ? (
        <div
          className="stillstrip"
          data-still
          style={{ backgroundImage: `url(${JSON.stringify(asset.posterUrl).slice(1, -1)})` }}
        />
      ) : null}
      {/* Audio tracks show the whole shape; a video clip with sound gets a
          band along its bottom edge, over the filmstrip, so a cut can be set
          on a pause without leaving the picture. */}
      {asset?.waveformUrl && (track.kind === 'audio' || asset.hasAudio) ? (
        <Waveform
          url={asset.waveformUrl}
          clip={clip}
          asset={asset}
          compact={track.kind === 'video' && !tonkurve}
          bahn={tonkurve ? TONKURVE_HOEHE : 0}
        />
      ) : null}
      {/* Die Marke des Übergangs steht seit 7.74d *zwischen* den Clips, in der
          Spur -- nicht im Clip. Hier bleibt sie nur für Zuschauer, die keine
          setzen dürfen: sie sollen trotzdem sehen, dass dort geblendet wird. */}
      {clip.transitionIn && !onUebergang ? (
        <div className="transition-badge">{Math.round(clip.transitionIn.duration * 10) / 10}s</div>
      ) : null}
      {/* Offene Hinweise an diesem Clip. Ohne die Marke müsste man das Blatt
          öffnen, um zu sehen, dass an einem Clip etwas angemerkt ist -- und
          genau das war die Meldung („Mir bringt das Hinweismenü nix“). */}
      {hinweise > 0 ? (
        <div className="hinweis-marke" data-hinweise={hinweise} title={hinweise === 1 ? 'Ein offener Hinweis' : `${hinweise} offene Hinweise`}>
          <Icon name="captions" size={10} />
          {hinweise > 1 ? hinweise : null}
        </div>
      ) : null}
      {clip.source.kind === 'text' ? (
        <TextPreview text={clip.source.text} style={clip.source.style} />
      ) : (
        <div className="clip-name">
          {/* Stumm: gezeichnet wie jedes andere Symbol, nicht als Emoji aus
              dem Stilblatt (7.17). */}
          {clip.muted ? <Icon name="mute" size={11} /> : null}
          {label}
        </div>
      )}
      {/* Der Pegelverlauf als Linie über dem Clip: eine Blende, die man sieht,
          ohne ein Blatt zu öffnen. Oben ist voll (200 %), unten still.
          `?? []`, weil ein Schnitt, der vor diesem Feld gespeichert wurde, es
          nicht hat -- und ein fehlendes Feld darf die Zeitleiste nicht
          umbringen, auch wenn die Schnittstelle es inzwischen nachträgt. */}
      {(clip.gainKeyframes ?? []).length > 1 && clip.duration > 0 ? (
        <svg className="pegel-linie" viewBox={`0 0 100 100`} preserveAspectRatio="none" aria-hidden="true">
          <polyline
            points={[...(clip.gainKeyframes ?? [])]
              .sort((a, b) => a.at - b.at)
              .map((k) => `${((k.at / clip.duration) * 100).toFixed(2)},${(100 - Math.min(100, (k.gain / 2) * 100)).toFixed(2)}`)
              .join(' ')}
          />
        </svg>
      ) : null}
      {/* Nach der Zeit sortiert und nach ihrem Platz benannt, nicht nach ihrer
          Stelle: ein Schlüssel, der sich beim Ziehen ändert, baut den Punkt
          in jedem Bild neu -- und mit ihm verliert der Finger den Griff. */}
      {[...(clip.gainKeyframes ?? [])].sort((a, b) => a.at - b.at).map((k, i) => (
        <div
          key={`pegel-${i}`}
          className="pegel-punkt"
          data-pegel-at={k.at}
          style={{
            left: Math.min(Math.max(clip.duration > 0 ? (k.at / clip.duration) * width : 0, 4), width - 4),
            top: `${Math.max(2, Math.min(96, 100 - Math.min(100, (k.gain / 2) * 100)))}%`,
          }}
          title={`Pegel bei ${k.at.toFixed(2)} s: ${Math.round(k.gain * 100)} %`}
          onPointerDown={(e) => {
            e.stopPropagation();
            const hoehe = e.currentTarget.parentElement?.getBoundingClientRect().height ?? 0;
            onPointerDown(e, 'pegel', clip, track.id, k.at, k.gain, hoehe);
          }}
          onClick={(e) => {
            e.stopPropagation();
            onJumpTo(clip.start + k.at);
          }}
        />
      ))}
      {/* Keyframes as diamonds on the clip's top edge -- animation you can
          see without opening a sheet. Tapping one puts the playhead on it;
          dragging one (on the selected clip) moves it in time. */}
      {clip.keyframes.map((k, i) => (
        <div
          key={`kf-${i}`}
          className={`keyframe-dot ${selected && keyframeWahl !== null && Math.abs(keyframeWahl - k.at) < 1e-6 ? 'gewaehlt' : ''}`.trim()}
          data-keyframe-at={k.at}
          // Held inside the clip: a keyframe at 0 or at the very end would
          // otherwise hang half over the rounded edge and be cut off.
          style={{ left: Math.min(Math.max(clip.duration > 0 ? (k.at / clip.duration) * width : 0, 6), width - 6) }}
          title={`Keyframe bei ${k.at.toFixed(2)} s`}
          onPointerDown={(e) => {
            e.stopPropagation();
            // Angewählt wird schon beim Aufsetzen, nicht erst beim Loslassen:
            // wer die Raute zieht, hat sie auch gemeint, und nach einem Zug
            // kommt gar kein „click“ mehr. Erst damit hat die Rücktaste etwas
            // Kleineres zu löschen als den ganzen Clip.
            onWaehleKeyframe(k.at);
            onPointerDown(e, 'keyframe', clip, track.id, k.at);
          }}
          onClick={(e) => {
            e.stopPropagation();
            onJumpTo(clip.start + k.at);
          }}
        />
      ))}
      {/* Die Rampe der Blenden über Bild und Wellenform: von unten links bis
          dahin, wo die Blende endet -- man sieht sie, ohne ein Blatt zu öffnen. */}
      {einblenden > 0 || ausblenden > 0 ? (
        <svg className="blende-rampe" viewBox={`0 0 ${width} 100`} preserveAspectRatio="none" aria-hidden="true" data-blende-rampe>
          {einblenden > 0 ? <polygon points={`0,0 ${Math.min(width, einblenden * zoom)},0 0,100`} /> : null}
          {ausblenden > 0 ? <polygon points={`${width},0 ${Math.max(0, width - ausblenden * zoom)},0 ${width},100`} /> : null}
        </svg>
      ) : null}
      {selected && onBlende ? (
        <>
          <div
            className={`blende-griff ein ${blendeZug?.art === 'ein' ? 'zieht' : ''}`.trim()}
            data-blende="ein"
            data-blende-wert={einblenden.toFixed(1)}
            style={{ left: Math.min(Math.max(einblenden * zoom, BLENDE_RAND), width / 2) }}
            title={`Einblenden: ${einblenden.toFixed(1)} s`}
            onPointerDown={blendeGriff('ein')}
            onClick={(e) => e.stopPropagation()}
          />
          <div
            className={`blende-griff aus ${blendeZug?.art === 'aus' ? 'zieht' : ''}`.trim()}
            data-blende="aus"
            data-blende-wert={ausblenden.toFixed(1)}
            style={{ left: Math.max(Math.min(width - ausblenden * zoom, width - BLENDE_RAND), width / 2) }}
            title={`Ausblenden: ${ausblenden.toFixed(1)} s`}
            onPointerDown={blendeGriff('aus')}
            onClick={(e) => e.stopPropagation()}
          />
          {blendeZug ? <div className="blende-wert">{blendeZug.art === 'ein' ? 'Einblenden' : 'Ausblenden'} {blendeZug.wert.toFixed(1)} s</div> : null}
        </>
      ) : null}
      {selected ? (
        <>
          <div className="trim-handle left" onPointerDown={(e) => onPointerDown(e, 'trim-start', clip, track.id)} />
          <div className="trim-handle right" onPointerDown={(e) => onPointerDown(e, 'trim-end', clip, track.id)} />
        </>
      ) : null}
    </div>
  );
}

const waveformCache = new Map<string, number[]>();

/**
 * Die Tonkurve eines Clips.
 *
 * Drei Gestalten: über die ganze Höhe (Tonspur), als schmales Band unten
 * (Bildspur, zugeklappt) und als eigene Bahn darunter, wenn die Bildspur
 * aufgeklappt ist (7.65). Nur die dritte taugt zum Schneiden nach Ton --
 * dafür ist sie da.
 */
function Waveform({
  url,
  clip,
  asset,
  compact = false,
  bahn = 0,
}: {
  url: string;
  clip: Clip;
  asset: AssetView;
  compact?: boolean;
  /** Höhe der eigenen Bahn unter dem Bild, in Punkten. 0 heisst: keine. */
  bahn?: number;
}): ReactElement {
  const [peaks, setPeaks] = useState<number[]>(() => waveformCache.get(url) ?? []);

  useEffect(() => {
    if (waveformCache.has(url)) return;
    let cancelled = false;
    void fetch(url)
      .then((r) => r.json() as Promise<{ peaks: number[] }>)
      .then((data) => {
        waveformCache.set(url, data.peaks);
        if (!cancelled) setPeaks(data.peaks);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [url]);

  if (peaks.length === 0) return <div />;
  const from = Math.floor((clip.sourceIn / Math.max(asset.durationSec, 0.01)) * peaks.length);
  const to = Math.ceil(((clip.sourceIn + clip.duration * clip.speed) / Math.max(asset.durationSec, 0.01)) * peaks.length);
  const slice = peaks.slice(Math.max(0, from), Math.max(from + 1, to));
  // In Dezibel, nicht im Zahlenwert: linear ist eine gewöhnliche Aufnahme eine
  // gerade Linie mit einem Balken dort, wo es einmal geknackt hat. Die
  // Rechnung steht in `kurve.ts` und ist dort geprüft.
  //
  // **Nur nach oben, von der Grundlinie aus** (Sascha, 28. Oktober: „bei
  // anderen Schnittprogrammen ist der Pegel in den Spuren nur nach oben
  // gerichtet. Die Stereo-Sache sehen wir ja dann im Messwerkzeug“). Bis
  // dahin war sie um die Mitte gespiegelt -- doppelt so viel Fläche für
  // dieselbe Aussage.
  const hoch = slice.map((p) => kurvenHoehe(p));
  const points = hoch.map((p, i) => `${(i / Math.max(1, hoch.length - 1)) * 100},${100 - p * 92}`);
  const grund = ['100,100', '0,100'];

  return (
    <svg
      className={`waveform ${compact ? 'compact' : ''} ${bahn ? 'bahn' : ''}`.replace(/\s+/g, ' ').trim()}
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      aria-hidden="true"
      data-tonkurve={bahn ? 'bahn' : compact ? 'band' : 'voll'}
      style={bahn ? { top: 'auto', bottom: 0, height: bahn } : undefined}
    >
      <polygon points={[...points, ...grund].join(' ')} fill="var(--wave)" opacity={compact ? 0.8 : 0.85} />
    </svg>
  );
}

const filmstripImages = new Map<string, HTMLImageElement>();

/**
 * The frames of a clip, each in its own proportion. The strip the worker
 * made is a grid of frames in one picture, and stretching it to the clip's
 * width made every frame as wide as the clip's share of the source -- a
 * train squeezed to a ribbon. Here each slot takes the frame nearest its
 * moment, at the frame's own aspect, so a clip shows as many whole frames
 * as fit, and zooming in shows more of them rather than the same one wider.
 */
/**
 * Der Filmstreifen wird gezeichnet, nicht gedehnt.
 *
 * `hoehe` steht hier als Eigenschaft und nicht nur als gemessene Größe: sonst
 * merkt der Zeichenschritt nichts davon, dass die Spur höher geworden ist --
 * die Leinwand behält ihr altes Bild, und das Stilblatt zieht es in die Länge.
 * (Sascha, 7. September: „der Anfasser skaliert die Timeline irgendwie“.)
 */
function Filmstrip({ url, clip, asset, width, hoehe }: { url: string; clip: Clip; asset: AssetView; width: number; hoehe: number }): ReactElement {
  const ref = useRef<HTMLCanvasElement>(null);
  const [image, setImage] = useState<HTMLImageElement | null>(() => filmstripImages.get(url) ?? null);

  useEffect(() => {
    const cached = filmstripImages.get(url);
    if (cached) {
      setImage(cached);
      return;
    }
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => {
      filmstripImages.set(url, img);
      setImage(img);
    };
    img.src = url;
  }, [url]);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas || !image || !image.naturalWidth) return;
    const height = canvas.clientHeight || hoehe || 50;
    const ratio = Math.min(2, window.devicePixelRatio || 1);
    canvas.width = Math.max(1, Math.round(width * ratio));
    canvas.height = Math.max(1, Math.round(height * ratio));
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(ratio, ratio);
    // How to cut the picture comes from the server, which knows the strip's
    // name; a signed media url does not carry it. Without one -- a strip
    // written by an older version -- the old single row is the reading.
    const grid = asset.filmstripGrid ?? filmstripGridOf('', asset.durationSec);
    const frameW = image.naturalWidth / grid.columns;
    const frameH = image.naturalHeight / grid.rows;
    const slotW = Math.max(8, (height * frameW) / frameH);
    const sourceLen = Math.max(clip.duration * clip.speed, 0.01);
    for (let x = 0; x < width; x += slotW) {
      // The moment in the source at the middle of this slot.
      const t = clip.sourceIn + ((x + slotW / 2) / width) * sourceLen;
      const index = filmstripIndex(grid, t);
      const sx = (index % grid.columns) * frameW;
      const sy = Math.floor(index / grid.columns) * frameH;
      ctx.drawImage(image, sx, sy, frameW, frameH, x, 0, slotW, height);
    }
  }, [image, url, width, hoehe, clip.sourceIn, clip.duration, clip.speed, asset.durationSec, asset.filmstripGrid]);

  return <canvas ref={ref} className="filmstrip" />;
}

/** A text clip shows its words the way they will look, not a name in a box. */
function TextPreview({ text, style }: { text: string; style: Clip['source'] extends infer S ? (S extends { kind: 'text'; style: infer T } ? T : never) : never }): ReactElement {
  const line = text.split('\n').find((l) => l.trim()) ?? '…';
  return (
    <div
      className="text-preview"
      style={{
        fontFamily: `'${style.fontFamily}', Poppins, sans-serif`,
        color: style.color,
        fontWeight: style.bold ? 700 : 500,
        textTransform: style.uppercase ? 'uppercase' : undefined,
        background: style.boxColor ? `${style.boxColor}${Math.round(style.boxOpacity * 255).toString(16).padStart(2, '0')}` : undefined,
        textShadow: style.shadow ? '0 1px 3px #000c' : undefined,
        WebkitTextStroke: style.outlineColor ? `0.5px ${style.outlineColor}` : undefined,
        textAlign: style.align,
      }}
    >
      {style.uppercase ? line.toUpperCase() : line}
    </div>
  );
}


/**
 * Der Name einer Spur. Ein Blatt und kein Feld in der Zeile: auf dem Telefon
 * schiebt die Tastatur alles hoch, und ein Feld, das man dabei nicht mehr
 * sieht, tippt niemand zu Ende.
 */
/**
 * Ein Name, in einem eigenen kleinen Blatt.
 *
 * Fuer die Spur und seit 7.14 auch fuer den Clip: ein Eingabefeld mitten in
 * der Zeitleiste waere auf dem Telefon unter der Tastatur verschwunden, und
 * zweimal dasselbe Blatt zu bauen waere zweimal dasselbe Blatt.
 */
function SpurName({
  wert,
  kennung,
  titel = 'Spur umbenennen',
  platzhalter = 'z. B. Kamera, Musik, Bauchbinden',
  hinweis = 'Leer lassen: wieder die Kennung.',
  onFertig,
  onAbbrechen,
}: {
  wert: string;
  kennung: string;
  titel?: string;
  platzhalter?: string;
  hinweis?: string;
  onFertig: (name: string) => void;
  onAbbrechen: () => void;
}): ReactElement {
  const [text, setText] = useState(wert);
  return (
    <>
      <div className="sheet-backdrop" onClick={onAbbrechen} />
      <div className="sheet" role="dialog" aria-label={titel}>
        <div className="grab" />
        <div className="sheet-header">
          <h2>{titel}</h2>
          <button className="ghost small" onClick={onAbbrechen}>
            Abbrechen
          </button>
        </div>
        <div className="sheet-body">
          <label className="field">
            <span>Name für {kennung}</span>
            <input
              value={text}
              autoFocus
              maxLength={80}
              placeholder={platzhalter}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onFertig(text.trim());
                if (e.key === 'Escape') onAbbrechen();
              }}
            />
            <p className="hint">{hinweis}</p>
          </label>
          <button className="primary" style={{ width: '100%' }} onClick={() => onFertig(text.trim())}>
            Fertig
          </button>
        </div>
      </div>
    </>
  );
}


/** Zwei Buchstaben, damit auf ein Fähnchen passt, wer dort steht. */
function initialen(name: string): string {
  const teile = name.trim().split(/\s+/).filter(Boolean);
  if (teile.length === 0) return '?';
  if (teile.length === 1) return teile[0]!.slice(0, 2).toUpperCase();
  return (teile[0]![0]! + teile[teile.length - 1]![0]!).toUpperCase();
}

/**
 * Die Angaben zum Medium hinter einem Clip (7.14).
 *
 * Eine Auskunft, kein Regler. Bisher musste man dafuer ins Blatt "Medien" und
 * dort die Zeile suchen -- am Clip ist es einen langen Druck entfernt.
 */
function ClipAngaben({ clipId, onClose }: { clipId: string; onClose: () => void }): ReactElement | null {
  const zeit = useZeitText();
  const timeline = useEditor((s) => s.timeline);
  const assets = useEditor((s) => s.assets);
  const clip = timeline.tracks.flatMap((t) => t.clips).find((c) => c.id === clipId);
  const quelle = clip?.source;
  const asset = quelle?.kind === 'asset' ? assets.find((a) => a.id === quelle.assetId) : undefined;
  if (!clip) return null;

  const groesse = (bytes: number): string => {
    if (!bytes) return '—';
    const mb = bytes / 1024 / 1024;
    return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
  };
  const zeilen: [string, string][] = [
    ['Im Schnitt', `${zeit(clip.start, true)} · ${clip.duration.toFixed(2)} s`],
  ];
  if (asset) {
    zeilen.push(['Datei', asset.name]);
    zeilen.push(['Art', asset.kind === 'video' ? 'Video' : asset.kind === 'audio' ? 'Ton' : 'Bild']);
    if (asset.width && asset.height) zeilen.push(['Format', `${asset.width} × ${asset.height}`]);
    if (asset.durationSec > 0) zeilen.push(['Länge der Datei', `${asset.durationSec.toFixed(2)} s`]);
    // Der Ausschnitt sagt mehr als die Laenge allein: er sagt, wo im Material
    // dieser Clip liegt.
    zeilen.push(['Ausschnitt ab', `${clip.sourceIn.toFixed(2)} s`]);
    if (clip.speed !== 1) zeilen.push(['Tempo', `${clip.speed.toFixed(2)}×`]);
    zeilen.push(['Größe', groesse(asset.sizeBytes ?? 0)]);
    zeilen.push(['Zustand', asset.status === 'ready' ? 'fertig aufbereitet' : asset.status]);
  } else {
    zeilen.push(['Medium', 'nicht mehr da']);
  }

  return (
    <>
      <div className="sheet-backdrop" onClick={onClose} />
      <div className="sheet" role="dialog" aria-label="Angaben zum Clip">
        <div className="grab" />
        <div className="sheet-header">
          <h2>{clip.label || 'Clip'}</h2>
          <button className="ghost small" onClick={onClose}>
            Fertig
          </button>
        </div>
        <div className="sheet-body">
          <div className="list" data-angaben>
            {zeilen.map(([was, wert]) => (
              <div key={was} className="row" style={{ justifyContent: 'space-between' }}>
                <span className="sub">{was}</span>
                <strong style={{ textAlign: 'right', wordBreak: 'break-all' }}>{wert}</strong>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
